import React, { useState, useEffect, useRef, useMemo } from 'react';
import { View, Text, ImageBackground, Image, TextInput, ScrollView, TouchableOpacity, Platform, Modal, StyleSheet, Clipboard, Linking, Animated, ActivityIndicator } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { getFirestore, doc, getDoc, updateDoc, collection, query, where, limit, getDocs, addDoc, deleteDoc, deleteField, collectionGroup, writeBatch, serverTimestamp } from 'firebase/firestore';
import { getAuth, signOut, updateProfile } from 'firebase/auth';
import { profileStyles } from '../styles/ProfileStyles';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useUser } from '../components/LoginPersistence';
import * as DocumentPicker from 'expo-document-picker';
import { addFileToRootFolder, addFileToUserUploads, removeFileFromUserUploads } from '../utils/fileManagerUtils';
import { VerifiedBadge } from '../components/VerifiedBadge';
import styled from 'styled-components';
import { Ionicons } from '@expo/vector-icons';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { adminStyles } from '../styles/adminStyles';
import { BREAKPOINTS, getResponsiveValue } from '../styles/breakpoints';
import { colors } from '../styles/theme';
import { MediaViewer } from '../components/MediaViewer';
import { LoadingScreen } from '../components/LoadingScreen';
import { LinearGradient } from 'expo-linear-gradient';
import LegacyMediaManager from '../components/LegacyMediaManager';
import ProfileTabbedMediaManager from '../components/ProfileTabbedMediaManager';
import { PROFILE_TAB_MIGRATION_VERSION } from '../utils/profileTabMigration';
import { useCommunityModal } from '../contexts/CommunityModalContext';
import { OrganizationsTab } from '../components/OrganizationsTab';
import { ProfileMyPostsTab } from '../components/ProfileMyPostsTab';
import { useVersionWidget } from '../contexts/VersionWidgetContext';
import { useTheme } from '../contexts/ThemeContext';
import { InterestsPicker } from '../components/InterestsPicker';
import { useTourPlannerNavVisible } from '../contexts/FeatureFlagsContext';
import { ProfileTemplateCard } from '../components/ProfileTemplateCard';
import { ProfileCardEditor } from '../components/ProfileCardEditor';
import { useInterestCatalog } from '../hooks/useInterestCatalog';
import { resolveInterestDisplayItems } from '../utils/interestUtils';
import { useCommunity } from '../contexts/CommunityContext';
import { useAppLayout } from '../contexts/AppLayoutContext';
import { getSubscriptionDisplayInfo } from '../utils/subscriptionEntitlements';
import { usePendingCredits } from '../hooks/usePendingCredits';
import { useUserFunds } from '../hooks/useUserFunds';
import { formatFundsCents } from '../constants/userFunds';
import { PLAN_IDS, formatCentsAsDollars } from '../constants/subscriptionPlans';
import { glossyFill, glossyWebStyle } from '../utils/calendarV2Prefs';

const DEFAULT_PROFILE_PHOTOS = [
  require('../../assets/profile_photos/koi.png'),
  require('../../assets/profile_photos/bear.png'),
];

const ACCOUNT_TYPES = [
  { label: 'Individual', value: 'individual' },
  { label: 'Business', value: 'business' },
  { label: 'Band', value: 'band' },
  { label: 'Organization', value: 'organization' }
];

const getRandomDefaultPhoto = () => {
  const randomIndex = Math.floor(Math.random() * DEFAULT_PROFILE_PHOTOS.length);
  return DEFAULT_PROFILE_PHOTOS[randomIndex];
};

const getFileType = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();

  // Image types
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
    return 'image';
  }

  // PDF
  if (extension === 'pdf') {
    return 'pdf';
  }

  // Audio types
  if (['mp3', 'wav', 'aac', 'flac', 'ogg', 'm4a', 'wma'].includes(extension)) {
    return 'audio';
  }

  // Video types
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
    return 'video';
  }

  // Default to file
  return 'file';
};


const ImageActionButton = styled.button`
  border-radius: 15px;
  border: none;
  color: #ffffff;
  padding: 8px;
  background-color: transparent;
  align-items: center;
  opacity: 0.5;
  justify-content: center;
  transition: all 0.2s ease;
  &:hover {
    cursor: pointer;
    background-color: rgba(0, 0, 0, 0.5);
    opacity: 1;
  }
`;


const USER_TYPES = {
  ADMIN: {
    label: 'Admin',
    value: 'admin',
    color: 'pink'
  },
  MEMBER: {
    label: 'Member',
    value: 'member',
    color: '#0bb5df' // Our standard verification blue
  },
  VENUE_EMPLOYEE: {
    label: 'Venue Employee',
    value: 'venue_employee',
    color: '#2ecc71' // Green
  },
  VENUE_OWNER: {
    label: 'Venue Owner',
    value: 'venue_owner',
    color: '#9b59b6' // Purple
  },
  ARTIST: {
    label: 'Artist',
    value: 'artist',
    color: '#e67e22' // Orange
  },
  BAND_MEMBER: {
    label: 'Band Member',
    value: 'band_member',
    color: '#f1c40f' // Yellow
  },
  MUSIC_INDUSTRY: {
    label: 'Music Industry Employee',
    value: 'music_industry',
    color: '#1abc9c' // Turquoise
  }
};

// New reusable component for the right card content
const RightCardContent = ({
  userData,
  isLibraryMigrated,
  activeTab,
  setActiveTab,
  renderLegacyLibraryTab,
  renderMediaTab,
  renderReportsTab,
  renderSavedVenues,
  renderRoomEqTab,
  renderToursTab,
  renderCommunityTab,
  toursTabVisible = true,
}) => {
  const isMobile = Platform.OS === 'web' && typeof window !== 'undefined' && window.innerWidth <= BREAKPOINTS.mobile;
  const { theme } = useTheme();

  if (!isLibraryMigrated) {
    return <>{renderLegacyLibraryTab()}</>;
  }

  return (
    <View style={{ width: '100%', gap: 0 }}>
      <View style={[
        profileStyles.tabContainer,
        isMobile && {
          justifyContent: 'center',
          paddingHorizontal: 2,
        },
      ]}>
        {[
          { id: 'media', label: 'My Media' },
          { id: 'reports', label: 'My Reports' },
          { id: 'roomEq', label: 'Saved Room Info' },
          { id: 'savedVenues', label: 'Saved Venues' },
          { id: 'community', label: 'Saved Community Posts' },
          { id: 'tours', label: 'Saved Tours' },
        ]
          .filter((tab) => tab.id !== 'tours' || toursTabVisible)
          .map((tab) => (
            <TouchableOpacity
              key={tab.id}
              style={[
                profileStyles.tabButton,
                {
                  backgroundColor: 'transparent',
                  borderWidth: 0,
                  marginBottom: 0,
                  marginRight: isMobile ? 2 : 4,
                  paddingVertical: isMobile ? 5 : 6,
                  paddingHorizontal: isMobile ? 6 : 8,
                  transform: activeTab === tab.id ? 'scale(0.98)' : 'scale(1)',
                  transition: 'all 0.15s ease',
                },
              ]}
              onPress={() => setActiveTab(tab.id)}
            >
              <Text style={[
                profileStyles.tabButtonText,
                {
                  color: activeTab === tab.id
                    ? USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df'
                    : theme.text.primary,
                  fontSize: isMobile ? 11 : 13,
                  fontWeight: activeTab === tab.id ? '600' : '500',
                  textDecoration: activeTab === tab.id ? 'underline' : 'none',
                  lineHeight: isMobile ? 14 : 16,
                  transition: 'all 0.15s ease',
                  ...(Platform.OS === 'web' && {
                    ':hover': {
                      color: USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df',
                    },
                  }),
                },
              ]}>
                {tab.label}
              </Text>
            </TouchableOpacity>
          ))}
      </View>
      {activeTab === 'media' ? renderMediaTab() :
        activeTab === 'reports' ? renderReportsTab() :
          activeTab === 'roomEq' ? renderRoomEqTab() :
            activeTab === 'savedVenues' ? renderSavedVenues() :
              activeTab === 'community' ? renderCommunityTab() :
                activeTab === 'tours' ? renderToursTab() :
                  null}
    </View>
  );
};

const PlaneButton = styled.button`
  ${profileStyles.communityButton}
`;

// Custom Dropdown Component for Profile
const ProfileDropdown = ({ options, value, onSelect, placeholder, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const dropdownHeight = useRef(new Animated.Value(0)).current;
  const dropdownOpacity = useRef(new Animated.Value(0)).current;
  const rotateValue = useRef(new Animated.Value(0)).current;

  const rotate = rotateValue.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '180deg']
  });

  useEffect(() => {
    console.log('Dropdown useEffect...');
    if (isOpen) {
      Animated.parallel([
        Animated.timing(dropdownHeight, {
          toValue: options.length * 50, // Approximate height per option
          duration: 200,
          useNativeDriver: false,
        }),
        Animated.timing(dropdownOpacity, {
          toValue: 1,
          duration: 200,
          useNativeDriver: false,
        }),
        Animated.timing(rotateValue, {
          toValue: 1,
          duration: 200,
          useNativeDriver: false,
        })
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(dropdownHeight, {
          toValue: 0,
          duration: 200,
          useNativeDriver: false,
        }),
        Animated.timing(dropdownOpacity, {
          toValue: 0,
          duration: 200,
          useNativeDriver: false,
        }),
        Animated.timing(rotateValue, {
          toValue: 0,
          duration: 200,
          useNativeDriver: false,
        })
      ]).start();
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    onSelect(option.value);
    setIsOpen(false);
    setIsFocused(false);
  };

  const selectedOption = options.find(option => option.value === value);

  return (
    <View style={profileStyles.dropdownContainer}>
      <TouchableOpacity
        style={[
          profileStyles.dropdownButton,
          {
            opacity: disabled ? 0.7 : 1,
            borderColor: isFocused || isOpen ? colors.purpleReplacement : 'rgba(255, 255, 255, 0.1)'
          }
        ]}
        onPress={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
            setIsFocused(!isFocused);
          }
        }}
        disabled={disabled}
      >
        <Text style={[
          profileStyles.dropdownButtonText,
          { color: '#ffffff' }
        ]}>
          {selectedOption ? selectedOption.label : placeholder}
        </Text>
        <Animated.View style={{ transform: [{ rotate }] }}>
          <Ionicons name="chevron-down" size={16} color="#ffffff80" />
        </Animated.View>
      </TouchableOpacity>

      <Animated.View style={[
        profileStyles.dropdownList,
        {
          height: dropdownHeight,
          opacity: dropdownOpacity,
        }
      ]}>
        <ScrollView style={{ flex: 1 }}>
          {options.map((option) => (
            <TouchableOpacity
              key={option.value}
              style={[
                profileStyles.dropdownItem,
                {
                  backgroundColor: value === option.value ? 'rgba(74, 144, 226, 0.1)' : 'transparent',
                }
              ]}
              onPress={() => handleSelect(option)}
            >
              <Text style={[
                profileStyles.dropdownItemText,
                {
                  color: '#ffffff',
                }
              ]}>
                {option.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </Animated.View>
    </View>
  );
};

export function Profile({ navigation, route }) {
  const { user: userData, setUser } = useUser();
  const toursTabVisible = useTourPlannerNavVisible(userData);
  const { openMessaging } = useCommunityModal();
  const { refreshEntities } = useCommunity();
  const { mobileNavContentWidth } = useAppLayout();
  const { visible: showVersionWidget, setVisible: setShowVersionWidget, isAdmin: isAdminUser } = useVersionWidget();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [userTypeInput, setUserTypeInput] = useState('');
  const [accountType, setAccountType] = useState('');
  const [isHovering, setIsHovering] = useState(false);
  const [activeTab, setActiveTab] = useState('media');
  const [reports, setReports] = useState([]);
  const [savedVenueImages, setSavedVenueImages] = useState({});
  const auth = getAuth();
  const [isPublic, setIsPublic] = useState(false);
  const [allPostsAnonymized, setAllPostsAnonymized] = useState(false);
  const [isAnonymizing, setIsAnonymizing] = useState(false);
  const [hideFiltersVenueList, setHideFiltersVenueList] = useState(false);
  const [isUpdatingFiltersSetting, setIsUpdatingFiltersSetting] = useState(false);
  const [accountTab, setAccountTab] = useState('myAccount'); // 'myAccount' | 'organizations' | 'myPosts'
  const [organizations, setOrganizations] = useState([]);
  const [organizationInvitations, setOrganizationInvitations] = useState([]);
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgEmail, setNewOrgEmail] = useState('');
  const [newOrgDescription, setNewOrgDescription] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [profileUser, setProfileUser] = useState(null);
  const [isOwnProfile, setIsOwnProfile] = useState(true);
  const accountFunds = useUserFunds(isOwnProfile ? userData?.uid : null);
  const accountPendingCredits = usePendingCredits(isOwnProfile ? userData?.uid : null);

  useEffect(() => {
    if (!isOwnProfile && accountTab === 'myPosts') {
      setAccountTab('myAccount');
    }
  }, [isOwnProfile, accountTab]);

  const [showShareModal, setShowShareModal] = useState(false);
  const [profileLink, setProfileLink] = useState('');
  const linkCopied = useRef(false);
  const [bioHeight, setBioHeight] = useState(100);
  const [topCardHeight, setTopCardHeight] = useState(null);
  const [isMobile, setIsMobile] = useState(
    Platform.OS === 'web' && typeof window !== 'undefined' && window.innerWidth <= BREAKPOINTS.mobile
  );
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(null);
  const [isMediaViewerVisible, setIsMediaViewerVisible] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(null); // fileName to delete
  const [publicMedia, setPublicMedia] = useState({});
  const [profileCardTemplatePublic, setProfileCardTemplatePublic] = useState(false);
  const [profileCardEditing, setProfileCardEditing] = useState(false);
  const [playingAudio, setPlayingAudio] = useState(null);
  const [audioRefs, setAudioRefs] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [mediaManagerBounds, setMediaManagerBounds] = useState(null);
  const { theme, isDarkMode } = useTheme();
  const { interests: interestCatalog } = useInterestCatalog();

  const profileInterestItems = useMemo(
    () => resolveInterestDisplayItems(userData?.interests || [], interestCatalog),
    [userData?.interests, interestCatalog]
  );

  const isProfileLibraryMigrated =
    typeof userData?.profileTabRoots?.migrationVersion === 'number' &&
    userData.profileTabRoots.migrationVersion >= PROFILE_TAB_MIGRATION_VERSION;
  const hasMigratedProfileCard =
    !!userData?.profileCardTemplate &&
    Array.isArray(userData.profileCardTemplate.items) &&
    userData.profileCardTemplate.items.length > 0;

  useEffect(() => {
    if (!toursTabVisible && activeTab === 'tours') {
      setActiveTab('media');
    }
  }, [toursTabVisible, activeTab]);

  // Choose a stable default profile photo per user to prevent flashing on re-renders
  const defaultProfilePhoto = React.useMemo(() => getRandomDefaultPhoto(), [userData?.uid]);

  // Drag and drop handlers
  const handleDragStart = (item, type, event) => {
    const dragData = { ...item, dragType: type };
    setDraggedItem(dragData);
    setDragPosition({ x: event.nativeEvent.pageX, y: event.nativeEvent.pageY });
    // Set global variable for MediaManager to access
    window.draggedItem = dragData;
  };

  const handleDragMove = (event) => {
    if (draggedItem) {
      const x = event.nativeEvent?.pageX || event.pageX;
      const y = event.nativeEvent?.pageY || event.pageY;
      setDragPosition({ x, y });

      // Use stored bounds for collision detection
      if (mediaManagerBounds) {
        const isOver = x >= mediaManagerBounds.left &&
          x <= mediaManagerBounds.right &&
          y >= mediaManagerBounds.top &&
          y <= mediaManagerBounds.bottom;

        console.log('Drag position:', { x, y });
        console.log('MediaManager bounds:', mediaManagerBounds);
        console.log('Is over MediaManager:', isOver);

        if (isOver) {
          // Trigger drag over visual feedback
          if (window.mediaManagerDragOverHandler) {
            window.mediaManagerDragOverHandler(true);
          }
        } else {
          // Trigger drag leave visual feedback
          if (window.mediaManagerDragOverHandler) {
            window.mediaManagerDragOverHandler(false);
          }
        }
      } else {
        console.log('MediaManager bounds not available yet');
      }
    }
  };

  const handleDragEnd = (event) => {
    // Check if we're dropping over the MediaManager
    if (draggedItem && event) {
      const x = event.nativeEvent?.pageX || event.pageX;
      const y = event.nativeEvent?.pageY || event.pageY;

      // Use stored bounds for collision detection
      if (mediaManagerBounds) {
        const isOver = x >= mediaManagerBounds.left &&
          x <= mediaManagerBounds.right &&
          y >= mediaManagerBounds.top &&
          y <= mediaManagerBounds.bottom;

        console.log('Drop position:', { x, y });
        console.log('MediaManager bounds:', mediaManagerBounds);
        console.log('Is over MediaManager:', isOver);

        if (isOver) {
          // Trigger drop in MediaManager
          if (window.mediaManagerDropHandler) {
            console.log('Triggering drop in MediaManager');
            console.log('Dragged item:', draggedItem);
            window.mediaManagerDropHandler(draggedItem);
          }
        }
      } else {
        console.log('MediaManager bounds not available for drop, using fallback');
        // Fallback: trigger drop anyway
        if (window.mediaManagerDropHandler) {
          window.mediaManagerDropHandler(draggedItem);
        }
      }
    }

    setDraggedItem(null);
    setDragPosition({ x: 0, y: 0 });
    // Clear global variable
    window.draggedItem = null;

    // Reset drag over state
    if (window.mediaManagerDragOverHandler) {
      window.mediaManagerDragOverHandler(false);
    }
  };

  // Set up MediaManager bounds detection
  useEffect(() => {
    console.log('Setting up MediaManager bounds detection...');
    const updateMediaManagerBounds = () => {
      // Try to get MediaManager bounds using multiple methods
      console.log('Getting MediaManager bounds...');
      const element = window.mediaManagerElement ||
        document.querySelector('[data-media-manager="true"]') ||
        document.querySelector('.media-manager-container');

      if (element && typeof element.getBoundingClientRect === 'function') {
        const rect = element.getBoundingClientRect();
        setMediaManagerBounds({
          left: rect.left,
          right: rect.right,
          top: rect.top,
          bottom: rect.bottom
        });
      } else {
        // Fallback: estimate bounds based on screen position
        // This is a rough estimate for when DOM methods don't work
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        setMediaManagerBounds({
          left: screenWidth * 0.5, // Right half of screen
          right: screenWidth,
          top: screenHeight * 0.3, // Lower third of screen
          bottom: screenHeight
        });
      }
    };

    // Set up bounds initially and on resize
    updateMediaManagerBounds();
    window.addEventListener('resize', updateMediaManagerBounds);

    // // Also try to update bounds when MediaManager is available
    // const interval = setInterval(() => {
    //   if (!mediaManagerBounds) {
    //     updateMediaManagerBounds();
    //   }
    // }, 1000);

    // return () => {
    //   window.removeEventListener('resize', updateMediaManagerBounds);
    //   clearInterval(interval);
    // };
  }, []);

  // Add global mouse and touch move listeners for drag tracking
  useEffect(() => {
    const handleGlobalMouseMove = (e) => {
      if (draggedItem) {
        handleDragMove(e);
      }
    };

    const handleGlobalMouseUp = (e) => {
      if (draggedItem) {
        handleDragEnd(e);
      }
    };

    const handleGlobalTouchMove = (e) => {
      if (draggedItem) {
        setDragPosition({ x: e.touches[0].pageX, y: e.touches[0].pageY });
      }
    };

    if (draggedItem) {
      // Prevent text selection during drag
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
      document.body.style.mozUserSelect = 'none';
      document.body.style.msUserSelect = 'none';
      document.body.style.webkitTouchCallout = 'none';
      document.body.style.cursor = 'grabbing';

      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    } else {
      // Re-enable text selection when not dragging
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.mozUserSelect = '';
      document.body.style.msUserSelect = '';
      document.body.style.webkitTouchCallout = '';
      document.body.style.cursor = '';
    }

    return () => {
      // Cleanup: re-enable text selection
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.mozUserSelect = '';
      document.body.style.msUserSelect = '';
      document.body.style.webkitTouchCallout = '';
      document.body.style.cursor = '';

      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchmove', handleGlobalTouchMove);
    };
  }, [draggedItem]);

  useEffect(() => {
    // Check if we're viewing a specific user's profile via route params
    console.log('Loading user profile...');
    const viewUserId = route?.params?.userId;

    if (viewUserId && userData && viewUserId !== userData.uid) {
      setIsOwnProfile(false);
      loadUserProfile(viewUserId);
    } else if (userData) {
      setProfileUser(userData);
      setIsOwnProfile(true);
      setLoading(false);
      loadReports();
      loadSavedVenues();
      loadPublicMedia();
    } else {
      navigation.replace('Login');
    }
  }, [userData, navigation, route?.params?.userId]);

  // Initialize userTypeInput when userData changes
  useEffect(() => {
    console.log('Loading user type...');
    if (userData?.userType) {
      setUserTypeInput(userData.userType);
    }
  }, [userData?.userType]);

  // Initialize accountType when userData changes
  useEffect(() => {
    console.log('Loading account type...');
    if (userData?.accountType) {
      setAccountType(userData.accountType);
    }
  }, [userData?.accountType]);

  useEffect(() => {
    console.log('Loading privacy settings...');
    const loadPrivacySettings = async () => {
      try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userData.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          setIsPublic(data.isPublic ?? false);
          setProfileCardTemplatePublic(data.profileCardTemplatePublic ?? false);
          setAllPostsAnonymized(data.allPostsAnonymized ?? false);
          setHideFiltersVenueList(data.hideFiltersVenueList ?? false);
        }
      } catch (error) {
        console.error('Error loading privacy settings:', error);
      }
    };

    if (userData?.uid) {
      loadPrivacySettings();
    }
  }, [userData?.uid]);

  // Add a resize listener to detect mobile/desktop changes
  useEffect(() => {
    console.log('Loading resize listener...');
    if (typeof window !== 'undefined') {
      const handleResize = () => {
        setIsMobile(window.innerWidth <= BREAKPOINTS.mobile);
      };

      // window.addEventListener('resize', handleResize);
      // return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  const loadReports = async () => {
    try {
      const db = getFirestore();
      const reportsRef = collection(db, 'venue_reports');
      const q = query(reportsRef, where('userEmail', '==', userData.email));
      const querySnapshot = await getDocs(q);
      const reportsArray = [];

      for (const docSnapshot of querySnapshot.docs) {
        const reportData = docSnapshot.data();
        let venueData = null;
        if (reportData.venueId) {
          const venueRef = doc(db, 'venues', reportData.venueId);
          const venueDoc = await getDoc(venueRef);
          if (venueDoc.exists()) {
            venueData = venueDoc.data();
          }
        }
        reportsArray.push({
          id: docSnapshot.id,
          ...reportData,
          venue: venueData
        });
      }

      const sortedReports = reportsArray.sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;
        return b.createdAt.toDate() - a.createdAt.toDate();
      });

      console.log('Loaded reports:', sortedReports);
      setReports(sortedReports);
    } catch (error) {
      console.error('Error loading reports:', error);
    }
  };

  const loadSavedVenues = async () => {
    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', userData.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const savedVenueImagesData = userDoc.data().savedVenueImages || {};
        setSavedVenueImages(savedVenueImagesData);
      } else {
        setSavedVenueImages({});
      }
    } catch (error) {
      console.error('Error loading saved venue images:', error);
      setSavedVenueImages({});
    }
  };

  const loadPublicMedia = async () => {
    try {
      if (userData?.publicMedia) {
        setPublicMedia(userData.publicMedia);
      }
    } catch (error) {
      console.error('Error loading public media:', error);
    }
  };

  const loadUserProfile = async (userId) => {
    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists() && userDoc.data().isPublic) {
        setProfileUser(userDoc.data());
        setLoading(false);
      } else {
        // Profile is private or doesn't exist
        alert('This profile is private or does not exist');
        navigation.goBack();
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      alert('Error loading profile. Please try again.');
      navigation.goBack();
    }
  };

  if (loading || !userData) {
    return (
      <View style={[profileStyles.container, { backgroundColor: theme.background.primary }]}>
        <BackgroundVideo />
        <View style={[profileStyles.overlay, { backgroundColor: theme.background.primary }]}>
          <Text style={profileStyles.loadingText}>Loading...</Text>
        </View>
      </View>
    );
  }

  const handleProfilePhotoUpload = async () => {
    if (!userData?.uid) {
      console.error('No user ID found');
      alert('Please log in again');
      return;
    }

    try {
      const result = await DocumentPicker.getDocumentAsync({ type: 'image/*' });
      if (!result.canceled && result.assets && result.assets.length > 0) {
        const file = result.assets[0];
        const fileUri = file.uri;
        const response = await fetch(fileUri);
        const fileBlob = await response.blob();

        const storage = getStorage();
        const fileExt = file.name.split('.').pop();
        const fileName = `${userData.uid}.${fileExt}`;
        const storageRef = ref(storage, `userProfilePhotos/${fileName}`);

        // Upload to Firebase Storage
        await uploadBytes(storageRef, fileBlob);
        const photoURL = await getDownloadURL(storageRef);

        // Update Firestore document
        const db = getFirestore();
        const userRef = doc(db, 'users', userData.uid);
        await updateDoc(userRef, { photoURL });

        // Update local state
        setUser(prev => ({
          ...prev,
          photoURL
        }));

        alert('Profile photo updated successfully!');
      }
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      alert('Failed to update profile photo. Please try again.');
    }
  };

  const handleUserTypeUpdate = async () => {
    // Block admin user type
    if (userTypeInput.toLowerCase() === 'admin') {
      alert('You cannot set your user type to "admin". Please choose a different user type.');
      setUserTypeInput(userData.userType);
      return;
    }

    if (!userData?.uid) {
      console.error('No user ID found');
      alert('Please log in again');
      return;
    }

    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', userData.uid);
      await updateDoc(userRef, { userType: userTypeInput });

      // Update local state
      setUser(prev => ({
        ...prev,
        userType: userTypeInput
      }));

    } catch (error) {
      console.error('Error updating user type:', error);
      alert('Failed to update user type. Please try again.');
    }
  };

  const handleAccountTypeUpdate = async (newAccountType) => {
    if (!userData?.uid) {
      console.error('No user ID found');
      alert('Please log in again');
      return;
    }

    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', userData.uid);
      await updateDoc(userRef, { accountType: newAccountType });

      // Update local state
      setUser(prev => ({
        ...prev,
        accountType: newAccountType
      }));

      setAccountType(newAccountType);
    } catch (error) {
      console.error('Error updating account type:', error);
      alert('Failed to update account type. Please try again.');
    }
  };

  const handleMediaUpload = async () => {
    try {
      console.log('Opening document picker...');
      const result = await DocumentPicker.getDocumentAsync({ type: 'image/*' });
      console.log('Document picker result:', result);
      if (!result.canceled && result.assets && result.assets.length > 0) {
        const file = result.assets[0];
        const fileUri = file.uri;
        console.log('File URI:', fileUri);
        const response = await fetch(fileUri);
        const fileBlob = await response.blob();
        console.log('File blob created:', fileBlob);
        const storage = getStorage();
        const storageRef = ref(storage, `userMedia/${userData.uid || 'defaultUID'}/${file.name}`);
        await uploadBytes(storageRef, fileBlob);
        console.log('File uploaded to storage:', storageRef.fullPath);
        const downloadURL = await getDownloadURL(storageRef);
        console.log('Download URL obtained:', downloadURL);

        // Update Firestore user document
        const db = getFirestore();
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('email', '==', userData.email), limit(1));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const userDoc = querySnapshot.docs[0].ref;
          const newMedia = { ...userData.media, [file.name]: downloadURL };
          await updateDoc(userDoc, {
            media: newMedia,
          });
          console.log('User document updated with new media URL');

          // Update local state
          setUser((prevData) => ({
            ...prevData,
            media: newMedia,
          }));
          console.log('Local state updated with new media URL');

          await addFileToRootFolder(userData.uid, file.name, downloadURL, 'profile_media');
        }
      }
    } catch (error) {
      console.error('Error uploading media:', error);
    }
  };

  const handleVenueImageUpload = async (venueId) => {
    try {
      console.log('Opening document picker for venue image...');
      const result = await DocumentPicker.getDocumentAsync({ type: 'image/*' });
      console.log('Document picker result:', result);

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const file = result.assets[0];
        const fileUri = file.uri;
        console.log('File URI:', fileUri);

        const response = await fetch(fileUri);
        const fileBlob = await response.blob();
        console.log('File blob created:', fileBlob);

        const storage = getStorage();
        const fileExt = file.name.split('.').pop();
        const fileName = `${venueId}_${Date.now()}.${fileExt}`;
        const storageRef = ref(storage, `savedVenueImages/${userData.uid}/${fileName}`);

        await uploadBytes(storageRef, fileBlob);
        console.log('File uploaded to storage:', storageRef.fullPath);

        const downloadURL = await getDownloadURL(storageRef);
        console.log('Download URL obtained:', downloadURL);

        // Update Firestore user document
        const db = getFirestore();
        const userRef = doc(db, 'users', userData.uid);
        const newSavedVenueImages = { ...savedVenueImages, [venueId]: downloadURL };

        await updateDoc(userRef, {
          savedVenueImages: newSavedVenueImages,
        });
        console.log('User document updated with new venue image URL');

        // Update local state
        setSavedVenueImages(newSavedVenueImages);
        console.log('Local state updated with new venue image URL');

        // Update userData to reflect the new savedVenueImages
        setUser(prev => ({
          ...prev,
          savedVenueImages: newSavedVenueImages
        }));

        alert('Venue image uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading venue image:', error);
      alert('Failed to upload venue image. Please try again.');
    }
  };

  const handleVenueImageDelete = async (venueId) => {
    try {
      if (savedVenueImages[venueId]) {
        // Delete from Firebase Storage
        const storage = getStorage();
        // Extract filename from URL to delete from storage
        const imageUrl = savedVenueImages[venueId];
        const urlParts = imageUrl.split('/');
        const fileName = urlParts[urlParts.length - 1].split('?')[0];
        const storageRef = ref(storage, `savedVenueImages/${userData.uid}/${fileName}`);

        try {
          await deleteObject(storageRef);
        } catch (storageError) {
          console.log('Storage file may not exist, continuing with Firestore update:', storageError);
        }

        // Update Firestore document
        const db = getFirestore();
        const userRef = doc(db, 'users', userData.uid);
        const newSavedVenueImages = { ...savedVenueImages };
        delete newSavedVenueImages[venueId];

        await updateDoc(userRef, {
          savedVenueImages: newSavedVenueImages
        });

        // Update local state
        setSavedVenueImages(newSavedVenueImages);

        // Update userData to reflect the new savedVenueImages
        setUser(prev => ({
          ...prev,
          savedVenueImages: newSavedVenueImages
        }));

        alert('Venue image deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting venue image:', error);
      alert('Failed to delete venue image. Please try again.');
    }
  };

  const handlePasswordReset = async () => {
    const auth = getAuth();
    if (emailInput === userData.email) {
      try {
        await auth.sendPasswordResetEmail(userData.email);
        alert('Password reset link sent to your email.');
        setShowPasswordResetModal(false);
      } catch (error) {
        console.error('Error sending password reset email:', error);
      }
    } else {
      alert('Email does not match. Please try again.');
    }
  };

  const handleSave = async () => {
    try {
      // setLoading(true);
      const db = getFirestore();
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', userData.email), limit(1));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.docs[0].data().username !== userData.username) {
        const usernameQuery = query(usersRef, where('username', '==', userData.username), limit(1));
        const usernameQuerySnapshot = await getDocs(usernameQuery);
        if (!usernameQuerySnapshot.empty) {
          setUser(prev => ({
            ...prev,
            username: querySnapshot.docs[0].data().username
          }));
          alert('Username already exists. Please try again.');
          return;
        }
      }

      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0].ref;
        await updateDoc(userDoc, {
          firstName: userData.firstName,
          lastName: userData.lastName,
          username: userData.username,
          title: userData.title || '',
          occupation: userData.occupation || '',
          workplace: userData.workplace || '',
          location: userData.location || '',
          bio: userData.bio || '',
          socialLinks: userData.socialLinks || [],
          publicMedia: publicMedia || {},
          interests: userData.interests || [],
        });
      } else {
        throw new Error('User document not found');
      }
    } catch (error) {
      console.error('Error updating user data:', error);
    } finally {
      // setLoading(false);
    }
  };

  const handleAddSocialLink = () => {
    const currentLinks = Array.isArray(userData.socialLinks) ? userData.socialLinks : [];
    setUser(prev => ({
      ...prev,
      socialLinks: [...currentLinks, '']
    }));
  };

  const handleUpdateSocialLink = (index, value) => {
    const currentLinks = Array.isArray(userData.socialLinks) ? userData.socialLinks : [];
    const updatedLinks = [...currentLinks];
    updatedLinks[index] = value;
    setUser(prev => ({
      ...prev,
      socialLinks: updatedLinks
    }));
  };

  const handleRemoveSocialLink = (index) => {
    const currentLinks = Array.isArray(userData.socialLinks) ? userData.socialLinks : [];
    const updatedLinks = [...currentLinks];
    updatedLinks.splice(index, 1);
    setUser(prev => ({
      ...prev,
      socialLinks: updatedLinks
    }));
  };

  const handleSignOut = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      navigation.replace('Coming Soon');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };


  const handleSubscribe = () => {
    navigation.navigate('Plans');
  };

  const handleManageSubscription = () => {
    navigation.navigate('Wallet', { tab: 'membership' });
  };

  const handleMediaDelete = async (fileName) => {
    try {
      console.log(fileName);
      const storage = getStorage();
      const storageRef = ref(storage, `userMedia/${userData.uid}/${fileName}`);
      const mediaUrl = await getDownloadURL(storageRef);
      await deleteObject(storageRef);

      // Update Firestore document
      const db = getFirestore();
      const userRef = doc(db, 'users', userData.uid);
      const newMedia = { ...userData.media };
      console.log(newMedia[fileName]);
      delete newMedia[fileName];
      await updateDoc(userRef, { media: newMedia });

      // Update local state
      setUser(prev => ({
        ...prev,
        media: newMedia
      }));

      // Remove from User Uploads folder
      try {
        // Get the file URL from the media object before deletion
        if (mediaUrl) {
          await removeFileFromUserUploads(userData.uid, mediaUrl);
          console.log('✅ Removed file from User Uploads folder');
        } else {
          console.log('No URL found for file, skipping User Uploads removal');
        }
      } catch (userUploadsError) {
        console.log('File may not exist in User Uploads folder:', userUploadsError);
        // Continue with deletion even if User Uploads removal fails
      }
    } catch (error) {
      console.error('Error deleting media:', error);
    }
  };

  const handlePublicMediaUpload = async (mediaType) => {
    try {
      // Check if we already have 3 media items
      if (publicMedia.media && publicMedia.media.length >= 3) {
        alert('You can only upload up to 3 media items.');
        return;
      }

      const result = await DocumentPicker.getDocumentAsync({
        type: '*/*' // Accept any file type
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const file = result.assets[0];
        const fileUri = file.uri;
        const response = await fetch(fileUri);
        const fileBlob = await response.blob();

        const storage = getStorage();
        const fileExt = file.name.split('.').pop();
        const fileName = `public_media_${Date.now()}.${fileExt}`;
        const storageRef = ref(storage, `publicMedia/${userData.uid}/${fileName}`);

        // Upload to Firebase Storage
        await uploadBytes(storageRef, fileBlob);
        const downloadURL = await getDownloadURL(storageRef);

        // Update Firestore user document
        const db = getFirestore();
        const userRef = doc(db, 'users', userData.uid);
        const newPublicMedia = { ...publicMedia };

        if (!newPublicMedia.media) {
          newPublicMedia.media = [];
        }

        const mediaItem = {
          id: `media_${Date.now()}`,
          name: file.name,
          url: downloadURL,
          type: 'media',
          uploadedAt: new Date().toISOString(),
          size: file.size || 0
        };

        newPublicMedia.media.push(mediaItem);

        await updateDoc(userRef, { publicMedia: newPublicMedia });

        // Update local state
        setPublicMedia(newPublicMedia);
        setUser(prev => ({
          ...prev,
          publicMedia: newPublicMedia
        }));

        // Add to User Uploads folder
        // await addFileToUserUploads(
        //   userData.uid,
        //   file.name,
        //   downloadURL,
        //   'profile_public_media',
        //   { mediaType: 'media', publicMedia: true }
        // );
        await addFileToUserUploads(
          userData.uid,
          file.name,
          downloadURL,
          'profile_public_media',
          { mediaType: 'media', publicMedia: true }
        );

      }
    } catch (error) {
      console.error(`Error uploading ${mediaType}:`, error);
      alert(`Failed to upload ${mediaType}. Please try again.`);
    }
  };

  const handlePublicMediaDelete = async (mediaId) => {
    try {
      const mediaItem = publicMedia.media?.find(item => item.id === mediaId);
      if (!mediaItem) return;

      // Delete from Firebase Storage
      const storage = getStorage();
      const urlParts = mediaItem.url.split('/');
      const fileName = urlParts[urlParts.length - 1].split('?')[0];
      const storageRef = ref(storage, `publicMedia/${userData.uid}/${fileName}`);

      try {
        await deleteObject(storageRef);
      } catch (storageError) {
        console.log('Storage file may not exist, continuing with Firestore update:', storageError);
      }

      // Remove from User Uploads folder
      try {
        await removeFileFromUserUploads(userData.uid, mediaItem.url);
        console.log('✅ Removed file from User Uploads folder');
      } catch (userUploadsError) {
        console.log('File may not exist in User Uploads folder:', userUploadsError);
        // Continue with deletion even if User Uploads removal fails
      }

      // Update Firestore document
      const db = getFirestore();
      const userRef = doc(db, 'users', userData.uid);
      const newPublicMedia = { ...publicMedia };
      newPublicMedia.media = newPublicMedia.media.filter(item => item.id !== mediaId);

      await updateDoc(userRef, { publicMedia: newPublicMedia });

      // Update local state
      setPublicMedia(newPublicMedia);
      setUser(prev => ({
        ...prev,
        publicMedia: newPublicMedia
      }));

      alert('Media deleted successfully!');
    } catch (error) {
      console.error('Error deleting public media:', error);
      alert('Failed to delete media. Please try again.');
    }
  };

  // Handle PDF download and open
  const handlePdfClick = async (url) => {
    try {
      await Linking.openURL(url);
    } catch (error) {
      Alert.alert('Error', 'Could not open PDF file');
    }
  };

  // Handle audio play/pause
  const handleAudioClick = (item) => {
    if (playingAudio === item.id) {
      // Stop current audio
      if (audioRefs[item.id]) {
        audioRefs[item.id].pause();
      }
      setPlayingAudio(null);
    } else {
      // Stop any currently playing audio
      if (playingAudio && audioRefs[playingAudio]) {
        audioRefs[playingAudio].pause();
      }

      // Play new audio
      const audio = new Audio(item.url);
      audio.play();
      setAudioRefs(prev => ({ ...prev, [item.id]: audio }));
      setPlayingAudio(item.id);

      // Handle audio end
      audio.onended = () => {
        setPlayingAudio(null);
      };
    }
  };

  // Handle video play
  const handleVideoClick = (url) => {
    try {
      Linking.openURL(url);
    } catch (error) {
      Alert.alert('Error', 'Could not open video file');
    }
  };

  const handlePrivacyToggle = async () => {
    try {
      const newIsPublic = !isPublic;
      const db = getFirestore();
      const userRef = doc(db, 'users', userData.uid);

      // Update Firestore first
      await updateDoc(userRef, {
        isPublic: newIsPublic
      });

      // Only update local state after successful Firestore update
      setIsPublic(newIsPublic);
      setUser(prev => ({
        ...prev,
        isPublic: newIsPublic
      }));

      // Optional: Show feedback to user
      console.log(`Profile visibility changed to ${newIsPublic ? 'public' : 'private'}`);
    } catch (error) {
      console.error('Error updating privacy settings:', error);
      // Optionally show error to user
      alert('Failed to update privacy settings. Please try again.');
    }
  };

  const handleProfileCardPublicToggle = async () => {
    if (!userData?.uid) return;
    try {
      const next = !profileCardTemplatePublic;
      const db = getFirestore();
      const userRef = doc(db, 'users', userData.uid);
      await updateDoc(userRef, {
        profileCardTemplatePublic: next,
        ...(next
          ? { profileCardDirectoryListedAt: serverTimestamp() }
          : { profileCardDirectoryListedAt: deleteField() }),
      });
      setProfileCardTemplatePublic(next);
      setUser((prev) => ({ ...prev, profileCardTemplatePublic: next }));
      refreshEntities?.();
    } catch (error) {
      console.error('Error updating profile card public toggle:', error);
      alert('Could not update profile card visibility. Please try again.');
    }
  };

  const handleHideFiltersToggle = async () => {
    try {
      const newHideFiltersState = !hideFiltersVenueList;
      const db = getFirestore();

      // Show loading indicator
      setIsUpdatingFiltersSetting(true);

      // Update user document to store the preference
      const userRef = doc(db, 'users', userData.uid);
      await updateDoc(userRef, {
        hideFiltersVenueList: newHideFiltersState
      });

      // Update local state
      setHideFiltersVenueList(newHideFiltersState);
      setUser(prev => ({
        ...prev,
        hideFiltersVenueList: newHideFiltersState
      }));

      setIsUpdatingFiltersSetting(false);
    } catch (error) {
      console.error('Error updating hide filters setting:', error);
      setIsUpdatingFiltersSetting(false);
      alert('Failed to update filter visibility setting. Please try again.');
    }
  };

  const handleBulkAnonymizeToggle = async () => {
    try {
      const newAnonymizedState = !allPostsAnonymized;
      const db = getFirestore();

      // Show loading indicator
      setIsAnonymizing(true);

      // Query all discussion posts by this user across all venues
      const discussionsQuery = query(
        collectionGroup(db, 'discussions'),
        where('userId', '==', userData.uid)
      );

      const querySnapshot = await getDocs(discussionsQuery);

      if (querySnapshot.empty) {
        setIsAnonymizing(false);
        return;
      }

      // Use batch writes to update all posts
      let batch = writeBatch(db);
      let batchCount = 0;
      const batchSize = 500; // Firestore batch limit
      let totalUpdated = 0;

      // Get user data for updating posts
      const userRef = doc(db, 'users', userData.uid);
      const userDoc = await getDoc(userRef);
      const userDataDoc = userDoc.exists() ? userDoc.data() : null;

      const DEFAULT_PROFILE_PHOTOS = [
        require('../../assets/profile_photos/koi.png'),
        require('../../assets/profile_photos/bear.png'),
      ];
      const getRandomDefaultPhoto = () => {
        const randomIndex = Math.floor(Math.random() * DEFAULT_PROFILE_PHOTOS.length);
        return DEFAULT_PROFILE_PHOTOS[randomIndex];
      };

      for (const docSnapshot of querySnapshot.docs) {
        const postRef = docSnapshot.ref;

        if (newAnonymizedState) {
          // Anonymize: set username to Anonymous, use default avatar
          batch.update(postRef, {
            isAnonymous: true,
            username: 'Anonymous',
            userAvatar: getRandomDefaultPhoto()
          });
        } else {
          // Un-anonymize: restore original username and avatar
          const originalUsername = userDataDoc?.username || userDataDoc?.firstName || 'Anonymous';
          const originalAvatar = userDataDoc?.photoURL || getRandomDefaultPhoto();
          batch.update(postRef, {
            isAnonymous: false,
            username: originalUsername,
            userAvatar: originalAvatar
          });
        }

        batchCount++;
        totalUpdated++;

        // Commit batch if we hit the limit
        if (batchCount >= batchSize) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }

      // Commit remaining updates
      if (batchCount > 0) {
        await batch.commit();
      }

      // Update user document to store the preference
      await updateDoc(userRef, {
        allPostsAnonymized: newAnonymizedState
      });

      // Update local state
      setAllPostsAnonymized(newAnonymizedState);
      setUser(prev => ({
        ...prev,
        allPostsAnonymized: newAnonymizedState
      }));

      setIsAnonymizing(false);
    } catch (error) {
      console.error('Error updating post anonymization:', error);
      setIsAnonymizing(false);
      alert('Failed to update post anonymization. Please try again.');
    }
  };

  const ProfilePhotoContainer = () => (
    <TouchableOpacity
      style={profileStyles.profileImageContainer}
      onPress={handleProfilePhotoUpload}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <Image
        source={userData?.photoURL ? { uri: userData.photoURL } : defaultProfilePhoto}
        style={profileStyles.profileImage}
        resizeMode="cover"
      />
      <View style={[
        profileStyles.profileImageOverlay,
        isHovering && profileStyles.profileImageOverlayHovered
      ]}>
        <Text style={profileStyles.profileImageOverlayText}>
          Upload Profile Photo
        </Text>
      </View>
    </TouchableOpacity>
  );

  const handleMediaPress = (index) => {
    setSelectedMediaIndex(index);
    setIsMediaViewerVisible(true);
  };

  const handlePublicMediaPress = (index) => {
    if (publicMedia.media) {
      setSelectedMediaIndex(index);
      setIsMediaViewerVisible(true);
    }
  };

  const handleMediaViewerClose = () => {
    setIsMediaViewerVisible(false);
    setSelectedMediaIndex(null);
  };

  const renderMediaTab = () => (
    <View style={{ width: '100%', minHeight: 400 }}>
      <ProfileTabbedMediaManager
        userData={userData}
        setUser={setUser}
        onEditReport={handleEditReport}
        onDeleteReport={handleDeleteReport}
        onViewVenue={(venue) => navigation.navigate('Venue', { venueId: venue.id })}
        onAddVenuePhoto={handleVenueImageUpload}
        onDeleteVenuePhoto={handleVenueImageDelete}
        onEditRoomEqSubmission={(sub) =>
          navigation.navigate('Venue', {
            venueId: sub.venueId,
            editRoomEqSubmissionId: sub.id,
            editRoomEqSubmission: sub,
          })
        }
        libraryTab="media"
        allowFileUpload
        allowProfileDropImport
      />
    </View>
  );

  const renderLegacyLibraryTab = () => (
    <View style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <View style={profileStyles.mediaGrid}>
        {userData?.media && Object.entries(userData.media).map(([fileName, url], index) => (
          <TouchableOpacity
            onPress={() => handleMediaPress(index)}
            key={index}
            style={profileStyles.mediaPreviewContainer}
            onTouchStart={(e) => handleDragStart({ name: fileName, url }, 'media', e)}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
            onMouseDown={(e) => handleDragStart({ name: fileName, url }, 'media', e)}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
          >
            <Image
              source={{ uri: url }}
              style={profileStyles.mediaPreviewImage}
            />
            <TouchableOpacity
              onPress={() => setShowDeleteConfirmModal(fileName)}
              style={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderRadius: 12,
                padding: 5,
                position: 'absolute',
                right: 5,
                top: 5,
                width: 24,
                height: 24,
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Text style={{ color: '#ffffff', fontSize: 16, fontWeight: 'bold' }}>×</Text>
            </TouchableOpacity>
          </TouchableOpacity>
        ))}
        {(!userData?.media || Object.entries(userData.media).length < 4) && (
          <TouchableOpacity
            onPress={handleMediaUpload}
            style={[
              profileStyles.mediaPreviewContainer,
              profileStyles.mediaAddButton,
              {
                backgroundColor: `${USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df'}20`,
                borderWidth: 3,
                borderColor: `${USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df'}30`,
                borderStyle: 'dashed'
              }
            ]}
          >
            <Text style={[profileStyles.mediaPreviewText, { color: `${USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df'}` }]}>+ Add Media</Text>
            <Text style={[profileStyles.mediaPreviewText, { color: `${USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df'}`, opacity: 0.7 }]}>
              {Object.entries(userData?.media || {}).length}/4
            </Text>
          </TouchableOpacity>
        )}

        <MediaViewer
          visible={isMediaViewerVisible}
          onClose={handleMediaViewerClose}
          media={publicMedia.media || userData?.media}
          currentIndex={selectedMediaIndex}
          onNavigate={setSelectedMediaIndex}
        />
      </View>

      <Text style={{
        fontFamily: 'VastagoGrotesk',
        fontSize: 13,
        color: theme.text.secondary,
        textAlign: 'center',
        marginTop: 4,
        opacity: 0.95,
        paddingHorizontal: 8,
      }}>
        Classic library (single view). After profile tab migration, My Media, Reports, and other tabs appear here.
      </Text>
      <View style={{ width: '100%', minHeight: 400, marginTop: 8 }}>
        <LegacyMediaManager
          userData={userData}
          setUser={setUser}
          onEditReport={handleEditReport}
          onDeleteReport={handleDeleteReport}
          onViewVenue={(venue) => navigation.navigate('Venue', { venueId: venue.id })}
          onAddVenuePhoto={handleVenueImageUpload}
          onDeleteVenuePhoto={handleVenueImageDelete}
          onEditRoomEqSubmission={(sub) =>
            navigation.navigate('Venue', {
              venueId: sub.venueId,
              editRoomEqSubmissionId: sub.id,
              editRoomEqSubmission: sub,
            })
          }
          allowFileUpload
          allowProfileDropImport
        />
      </View>
    </View>
  );

  const handleEditReport = (report) => {
    const id = report?.id || report?.reportId;
    if (!id) return;
    navigation.navigate('MyReports', { editingReportId: id, report: { ...report, id } });
  };

  const handleDeleteReport = async (report) => {
    try {
      if (window.confirm('Are you sure you want to delete this report? This action cannot be undone.')) {
        const db = getFirestore();
        const rid = report?.id || report?.reportId;
        if (!rid) return;

        await deleteDoc(doc(db, 'venue_reports', rid));

        if (report.venueId) {
          const venueReportsRef = collection(doc(db, 'venues', report.venueId), 'venue_reports');
          const q = query(venueReportsRef, where('reportId', '==', rid));
          const querySnapshot = await getDocs(q);

          querySnapshot.forEach(async (d) => {
            await deleteDoc(d.ref);
          });
        }

        const nextFiles = { ...(userData.files || {}) };
        let removedLib = false;
        for (const fid of Object.keys(nextFiles)) {
          const f = nextFiles[fid];
          if (f?.itemType === 'report' && f.data?.reportId === rid) {
            delete nextFiles[fid];
            removedLib = true;
          }
        }
        if (removedLib && userData?.uid) {
          await updateDoc(doc(db, 'users', userData.uid), { files: nextFiles });
          setUser({ ...userData, files: nextFiles });
        }

        setReports(reports.filter((r) => r.id !== rid));

        alert('Report deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      alert('Failed to delete report. Please try again.');
    }
  };

  const profileMediaManagerBase = {
    userData,
    setUser,
    onEditReport: handleEditReport,
    onDeleteReport: handleDeleteReport,
    onViewVenue: (venue) => navigation.navigate('Venue', { venueId: venue.id }),
    onAddVenuePhoto: handleVenueImageUpload,
    onDeleteVenuePhoto: handleVenueImageDelete,
    onEditRoomEqSubmission: (sub) => {
      const sid = sub?.id ?? sub?.submissionId ?? sub?.data?.submissionId;
      const vid = sub?.venueId ?? sub?.data?.venueId;
      navigation.navigate('Venue', {
        venueId: vid,
        editRoomEqSubmissionId: sid,
        editRoomEqSubmission: { ...sub, id: sid, venueId: vid },
      });
    },
  };

  const renderReportsTab = () => (
    <View style={{ width: '100%', flex: 1, minHeight: 400 }}>
      <ProfileTabbedMediaManager
        {...profileMediaManagerBase}
        libraryTab="reports"
        allowFileUpload={false}
        allowProfileDropImport
      />
    </View>
  );

  const renderSavedVenues = () => (
    <View style={{ width: '100%', flex: 1, minHeight: 420 }}>
      <ProfileTabbedMediaManager
        {...profileMediaManagerBase}
        libraryTab="savedVenues"
        allowFileUpload={false}
        allowProfileDropImport
      />
    </View>
  );

  const renderRoomEqTab = () => (
    <View style={{ width: '100%', flex: 1, minHeight: 400 }}>
      <ProfileTabbedMediaManager
        {...profileMediaManagerBase}
        libraryTab="roomEq"
        allowFileUpload={false}
        allowProfileDropImport={false}
      />
    </View>
  );

  const renderCommunityTab = () => (
    <View style={{ width: '100%', flex: 1, minHeight: 400 }}>
      <ProfileTabbedMediaManager
        {...profileMediaManagerBase}
        libraryTab="community"
        allowFileUpload={false}
        allowProfileDropImport={false}
      />
    </View>
  );

  const renderToursTab = () => (
    <View style={{ width: '100%', flex: 1, minHeight: 420 }}>
      <ProfileTabbedMediaManager
        {...profileMediaManagerBase}
        libraryTab="tours"
        allowFileUpload={false}
        allowProfileDropImport
      />
    </View>
  );

  const renderVerificationBadge = () => {
    const userTypeColor = USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df';
    return (
      <VerifiedBadge style={[
        profileStyles.verificationBadge,
        { backgroundColor: userTypeColor }
      ]} />
    );
  };

  // Update the share profile functionality to directly copy to clipboard
  const shareProfile = () => {
    const currentUser = isOwnProfile ? userData : profileUser;
    if (!currentUser) return;

    const shareUrl = `${window.location.origin}/profile/${currentUser.uid}`;
    Clipboard.setString(shareUrl);

    // Show visual feedback
    alert('Profile link copied to clipboard!');
  };

  const copyProfileLink = () => {
    Clipboard.setString(profileLink);
    linkCopied.current = true;
    setTimeout(() => {
      linkCopied.current = false;
    }, 3000);
    alert('Profile link copied to clipboard!');
  };

  return (
    <View style={[profileStyles.container, { backgroundColor: theme.background.primary }]}>
      <BackgroundVideo />
      <View style={[profileStyles.overlay, { backgroundColor: theme.background.primary }]}>
        {/* Drag Indicator */}
        {draggedItem && (
          <View style={{
            position: 'fixed',
            left: dragPosition.x - 50,
            top: dragPosition.y - 50,
            width: 100,
            height: 100,
            backgroundColor: 'rgba(151, 255, 17, 0.9)',
            borderRadius: 10,
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            pointerEvents: 'none',
            ...(Platform.OS === 'web' ? { boxShadow: '0 8px 16px rgba(0,0,0,0.3)' } : {
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 0.3,
              shadowRadius: 8,
              elevation: 10,
            }),
          }}>
            <Ionicons
              name={draggedItem.dragType === 'venue' ? 'location' : 'document-text'}
              size={24}
              color="#ffffff"
            />
            <Text style={{
              color: '#ffffff',
              fontSize: 10,
              fontFamily: 'Glacial Indifference',
              textAlign: 'center',
              marginTop: 4,
            }} numberOfLines={1}>
              {draggedItem.dragType === 'venue' ? draggedItem.name : draggedItem.venueName}
            </Text>
          </View>
        )}
        <ScrollView
          style={{ width: '100%' }}
          contentContainerStyle={{
            alignItems: 'center',
            paddingTop: 12,
            paddingBottom: 20,
          }}
        >
          <View style={{
            width: '100%',
            maxWidth: 1400,
            paddingHorizontal: getResponsiveValue({
              mobile: 0,
              tablet: 10,
              medium: 10,
              laptop: 20,
              desktop: 20,
            }),
            marginHorizontal: 'auto',
            paddingTop: 0,
          }}>
            <View style={profileStyles.profileGrid}>
              <View style={profileStyles.profileColumnLeft}>
                <View
                  onLayout={(e) => setTopCardHeight(e.nativeEvent.layout.height)}
                  style={[
                    !hasMigratedProfileCard && profileStyles.profileTopLeftCard,
                    hasMigratedProfileCard && {
                      width: '100%',
                      position: 'relative',
                      alignItems: 'stretch',

                    },
                    !hasMigratedProfileCard && {
                      backgroundColor: 'rgb(255, 237, 177)',
                      borderWidth: 3,
                      borderColor: isPublic ? '#4caf50' : isPublic === false ? '#ff3b30' : '#666769',
                      borderStyle: 'solid',
                    },
                  ]}
                >
                  {!hasMigratedProfileCard && (
                    <PlaneButton
                      style={profileStyles.communityButton}
                      onClick={() => {
                        // Open the community modal and navigate to messages tab
                        // Note: Users cannot message themselves, so this just opens the messages tab
                        openMessaging(null, null);
                      }}
                    >
                      <Image source={require('../../assets/icons/paper-plane.png')} style={{ width: 24, height: 24 }} resizeMode="contain" />
                    </PlaneButton>
                  )}
                  {hasMigratedProfileCard ? (
                    <View style={{
                      width: mobileNavContentWidth > 0 ? mobileNavContentWidth : '100%',
                      maxWidth: '100%',
                      alignSelf: 'center',
                    }}>
                      <View style={{
                        borderWidth: 3,
                        borderRadius: 10,
                        borderStyle: 'solid',
                        borderColor: profileCardTemplatePublic ? '#4caf50' : '#ff3b30',
                        padding: 12,
                        backdropFilter: 'blur(20px)',
                        ...(Platform.OS === 'web'
                          ? { boxShadow: '0 4px 8px rgba(0,0,0,0.25)' }
                          : {
                            shadowColor: '#000',
                            shadowOffset: { width: 0, height: 4 },
                            shadowOpacity: 0.25,
                            shadowRadius: 4,
                            elevation: 4,
                          }),
                      }}>
                        {isOwnProfile ? (
                          profileCardEditing ? (
                            <ProfileCardEditor
                              embedded
                              interestEmojis={profileInterestItems.map((i) => i.emoji)}
                              interestItems={profileInterestItems}
                              saveLabel="Save profile card"
                              onSaved={() => setProfileCardEditing(false)}
                            />
                          ) : (
                            <ProfileTemplateCard
                              template={userData.profileCardTemplate}
                              onPress={() => {}}
                              showMessageButton={false}
                              interestItems={profileInterestItems}
                              showInterestEmojis={
                                userData.profileCardTemplate?.showInterestsOnCard !== false &&
                                profileInterestItems.length > 0
                              }
                              activeOpacity={1}
                            />
                          )
                        ) : (
                          <ProfileTemplateCard
                            template={userData.profileCardTemplate}
                            onPress={() => { }}
                            onMessagePress={() => openMessaging(null, null)}
                            showMessageButton
                            interestItems={profileInterestItems}
                            showInterestEmojis={
                              userData.profileCardTemplate?.showInterestsOnCard !== false &&
                              profileInterestItems.length > 0
                            }
                            activeOpacity={1}
                          />
                        )}
                      </View>
                      {isOwnProfile ? (
                        <View style={{ marginTop: 14, gap: 12 }}>
                          {!profileCardEditing ? (
                            <TouchableOpacity
                              style={[profileStyles.signOutButton, { backgroundColor: '#4e7de0', alignSelf: 'flex-start' }]}
                              onPress={() => setProfileCardEditing(true)}
                            >
                              <Text style={[profileStyles.signOutText, { color: '#fff' }]}>Edit Profile Card</Text>
                            </TouchableOpacity>
                          ) : null}
                          <InterestsPicker
                            selected={userData.interests || []}
                            onChange={(interests) => {
                              setUser((prev) => ({ ...prev, interests }));
                              setTimeout(handleSave, 0);
                            }}
                            label="Card interests"
                          />
                          <TouchableOpacity
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              backgroundColor: '#434252',
                              padding: 12,
                              borderRadius: 8,
                            }}
                            onPress={handleProfileCardPublicToggle}
                          >
                            <Text style={{ color: '#fff', fontSize: 14 }}>
                              {profileCardTemplatePublic
                                ? 'Public Profile Card (shown in Community Directory)'
                                : 'Private Profile Card'}
                            </Text>
                            <View style={{
                              width: 40,
                              height: 24,
                              backgroundColor: profileCardTemplatePublic ? '#2ecc71' : '#434252',
                              borderRadius: 12,
                              padding: 2,
                              justifyContent: 'center',
                              borderWidth: profileCardTemplatePublic ? 0 : 3,
                              borderColor: 'rgba(255,255,255,0.2)',
                            }}>
                              <View style={{
                                width: 20,
                                height: 20,
                                backgroundColor: '#fff',
                                borderRadius: 10,
                                transform: [{ translateX: profileCardTemplatePublic ? 16 : 0 }],
                                transition: 'transform 0.2s ease',
                              }} />
                            </View>
                          </TouchableOpacity>
                          {profileCardTemplatePublic ? (
                            <Text style={{ color: isDarkMode ? 'rgba(255,255,255,0.55)' : '#666', fontSize: 12, lineHeight: 18 }}>
                              When public, interest emojis appear on the top-right of your card in the Community Directory.
                            </Text>
                          ) : null}
                        </View>
                      ) : null}
                    </View>
                  ) : null}
                  {!hasMigratedProfileCard && (
                    <>
                      <ProfilePhotoContainer />
                      {userData?.verificationStatus === 'approved' && renderVerificationBadge()}
                      <Text style={[profileStyles.profileName, { color: '#000000' }]}>{`${userData.username}`}</Text>
                      <Text style={[profileStyles.userTypeText, {
                        color: USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df'
                      }]}>
                        {USER_TYPES[userData?.userType?.toUpperCase()]?.label || 'Member'}
                      </Text>
                      {userData?.verificationStatus && (
                        <Text style={[
                          profileStyles.verificationStatus,
                          userData.verificationStatus === 'approved' && [
                            profileStyles.verificationApproved,
                            { color: USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df' }
                          ],
                          userData.verificationStatus === 'pending' && profileStyles.verificationPending
                        ]}>
                          {userData.verificationStatus === 'approved' ? 'Verified User' : 'Verification Pending'}
                        </Text>
                      )}

                      <View style={{ width: '100%', marginTop: 20 }}>
                        <TextInput
                          style={[
                            profileStyles.profileInput,
                            {
                              backgroundColor: 'rgba(0, 0, 0, 0.1)',
                              color: '#000000',
                              height: 40,
                              paddingTop: 12,
                              paddingBottom: 12,
                              paddingLeft: 12,
                              borderRadius: 8,
                              marginBottom: 10
                            }
                          ]}
                          value={userData.title}
                          placeholder="Enter your title"
                          placeholderTextColor="rgba(0, 0, 0, 0.5)"
                          onChangeText={text => {
                            if (text.length <= 100) {
                              setUser({ ...userData, title: text });
                            }
                          }}
                          onBlur={handleSave}
                          maxLength={100}
                        />
                        <Text style={[profileStyles.characterCount, {
                          alignSelf: 'flex-end',
                          marginBottom: 10,
                          color: 'rgba(0, 0, 0, 0.5)',
                          fontSize: 12
                        }]}>
                          {(userData.title?.length || 0)}/100
                        </Text>

                        <TextInput
                          style={[
                            profileStyles.profileInput,
                            {
                              backgroundColor: 'rgba(0, 0, 0, 0.1)',
                              color: '#000000',
                              height: 40,
                              paddingTop: 12,
                              paddingBottom: 12,
                              paddingLeft: 12,
                              borderRadius: 8,
                              marginBottom: 10
                            }
                          ]}
                          value={userData.location}
                          placeholder="Enter your location"
                          placeholderTextColor="rgba(0, 0, 0, 0.5)"
                          onChangeText={text => {
                            if (text.length <= 100) {
                              setUser({ ...userData, location: text });
                            }
                          }}
                          onBlur={handleSave}
                          maxLength={100}
                        />
                        <Text style={[profileStyles.characterCount, {
                          alignSelf: 'flex-end',
                          marginBottom: 10,
                          color: 'rgba(0, 0, 0, 0.5)',
                          fontSize: 12
                        }]}>
                          {(userData.location?.length || 0)}/100
                        </Text>

                        <TextInput
                          style={[
                            profileStyles.profileInput,
                            {
                              backgroundColor: 'rgba(0, 0, 0, 0.1)',
                              color: '#000000',
                              height: Math.max(100, bioHeight),
                              textAlignVertical: 'top',
                              paddingTop: 12,
                              paddingBottom: 12,
                              paddingLeft: 12,
                              borderRadius: 8
                            }
                          ]}
                          value={userData.bio}
                          placeholder="Enter your bio"
                          placeholderTextColor="rgba(0, 0, 0, 0.5)"
                          onChangeText={text => {
                            if (text.length <= 500) {
                              setUser({ ...userData, bio: text });
                            }
                          }}
                          onContentSizeChange={(event) => {
                            setBioHeight(event.nativeEvent.contentSize.height);
                          }}
                          onBlur={handleSave}
                          multiline={true}
                          maxLength={500}
                        />
                        <Text style={[profileStyles.characterCount, {
                          alignSelf: 'flex-end',
                          marginTop: 5,
                          color: 'rgba(0, 0, 0, 0.5)',
                          fontSize: 12
                        }]}>
                          {(userData.bio?.length || 0)}/500
                        </Text>
                      </View>

                      {/* Public Media Section */}
                      <View style={{ width: '100%', marginTop: 20 }}>
                        <Text style={[profileStyles.sectionTitle, { color: '#000000', marginBottom: 10 }]}>Public Media</Text>

                        {/* Media Display Grid - 3 slots */}
                        <View style={{
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          gap: 10,
                          marginBottom: 15
                        }}>
                          {/* Media Slot 1 */}
                          <View style={{
                            flex: 1,
                            height: 80,
                            borderRadius: 8,
                            overflow: 'hidden',
                            position: 'relative'
                          }}>
                            {publicMedia.media && publicMedia.media[0] ? (
                              <TouchableOpacity
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  position: 'relative'
                                }}
                                onPress={() => handlePublicMediaPress(0)}
                                onTouchStart={(e) => handleDragStart({ name: publicMedia.media[0].name, url: publicMedia.media[0].url }, 'media', e)}
                                onTouchMove={handleDragMove}
                                onTouchEnd={handleDragEnd}
                                onMouseDown={(e) => handleDragStart({ name: publicMedia.media[0].name, url: publicMedia.media[0].url }, 'media', e)}
                                onMouseMove={handleDragMove}
                                onMouseUp={handleDragEnd}
                              >
                                {getFileType(publicMedia.media[0].name) === 'image' ? (
                                  <Image
                                    source={{ uri: publicMedia.media[0].url }}
                                    style={{ width: '100%', height: '100%' }}
                                    resizeMode="cover"
                                  />
                                ) : getFileType(publicMedia.media[0].name) === 'pdf' ? (
                                  <View style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#e74c3c',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column'
                                  }}>
                                    <Ionicons name="document-text" size={24} color="#ffffff" />
                                    <Text style={{
                                      color: '#ffffff',
                                      fontSize: 8,
                                      fontFamily: 'VastagoGrotesk',
                                      marginTop: 2,
                                      fontWeight: 'bold'
                                    }}>PDF</Text>
                                    <Ionicons name="download" size={12} color="#ffffff" style={{ marginTop: 2 }} />
                                  </View>
                                ) : getFileType(publicMedia.media[0].name) === 'audio' ? (
                                  <View style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#9b59b6',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column'
                                  }}>
                                    <View style={profileStyles.audioWaveContainer}>
                                      <View style={[profileStyles.audioWave, { height: 8 }]} />
                                      <View style={[profileStyles.audioWave, { height: 12 }]} />
                                      <View style={[profileStyles.audioWave, { height: 6 }]} />
                                      <View style={[profileStyles.audioWave, { height: 10 }]} />
                                      <View style={[profileStyles.audioWave, { height: 4 }]} />
                                    </View>
                                    {Platform.OS === 'web' && (
                                      <audio src={publicMedia.media[0].url} controls style={{ width: '90%', marginTop: 6 }} />
                                    )}
                                    <Text style={{
                                      color: '#ffffff',
                                      fontSize: 8,
                                      fontFamily: 'VastagoGrotesk',
                                      marginTop: 2,
                                      fontWeight: 'bold'
                                    }}>
                                      {playingAudio === publicMedia.media[0].id ? 'PAUSE' : 'PLAY'}
                                    </Text>
                                  </View>
                                ) : getFileType(publicMedia.media[0].name) === 'video' ? (
                                  <View style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#e67e22',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column'
                                  }}>
                                    <Ionicons name="play-circle" size={24} color="#ffffff" />
                                    <Text style={{
                                      color: '#ffffff',
                                      fontSize: 8,
                                      fontFamily: 'VastagoGrotesk',
                                      marginTop: 2,
                                      fontWeight: 'bold'
                                    }}>VIDEO</Text>
                                  </View>
                                ) : (
                                  <View style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#34495e',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column'
                                  }}>
                                    <Ionicons name="attach" size={24} color="#ffffff" />
                                    <Text style={{
                                      color: '#ffffff',
                                      fontSize: 8,
                                      fontFamily: 'VastagoGrotesk',
                                      marginTop: 2,
                                      fontWeight: 'bold'
                                    }}>FILE</Text>
                                  </View>
                                )}

                                {/* Delete button */}
                                <TouchableOpacity
                                  onPress={() => handlePublicMediaDelete(publicMedia.media[0].id)}
                                  style={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    backgroundColor: 'rgba(255, 0, 0, 0.8)',
                                    borderRadius: 10,
                                    width: 20,
                                    height: 20,
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Text style={{ color: '#ffffff', fontSize: 12, fontWeight: 'bold' }}>×</Text>
                                </TouchableOpacity>
                              </TouchableOpacity>
                            ) : (
                              <TouchableOpacity
                                onPress={() => handlePublicMediaUpload('media1')}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  backgroundColor: `${USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df'}20`,
                                  borderRadius: 8,
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderWidth: 2,
                                  borderColor: `${USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df'}40`,
                                  borderStyle: 'dashed'
                                }}
                              >
                                <Ionicons
                                  name="add-circle"
                                  size={20}
                                  color={USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df'}
                                />
                                <Text style={{
                                  color: USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df',
                                  fontFamily: 'VastagoGrotesk',
                                  fontSize: 10,
                                  marginTop: 2,
                                  textAlign: 'center'
                                }}>Add Media</Text>
                              </TouchableOpacity>
                            )}
                          </View>

                          {/* Media Slot 2 */}
                          <View style={{
                            flex: 1,
                            height: 80,
                            borderRadius: 8,
                            overflow: 'hidden',
                            position: 'relative'
                          }}>
                            {publicMedia.media && publicMedia.media[1] ? (
                              <TouchableOpacity
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  position: 'relative'
                                }}
                                onPress={() => handlePublicMediaPress(1)}
                                onTouchStart={(e) => handleDragStart({ name: publicMedia.media[1].name, url: publicMedia.media[1].url }, 'media', e)}
                                onTouchMove={handleDragMove}
                                onTouchEnd={handleDragEnd}
                                onMouseDown={(e) => handleDragStart({ name: publicMedia.media[1].name, url: publicMedia.media[1].url }, 'media', e)}
                                onMouseMove={handleDragMove}
                                onMouseUp={handleDragEnd}
                              >
                                {getFileType(publicMedia.media[1].name) === 'image' ? (
                                  <Image
                                    source={{ uri: publicMedia.media[1].url }}
                                    style={{ width: '100%', height: '100%' }}
                                    resizeMode="cover"
                                  />
                                ) : getFileType(publicMedia.media[1].name) === 'pdf' ? (
                                  <View style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#e74c3c',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column'
                                  }}>
                                    <Ionicons name="document-text" size={24} color="#ffffff" />
                                    <Text style={{
                                      color: '#ffffff',
                                      fontSize: 8,
                                      fontFamily: 'VastagoGrotesk',
                                      marginTop: 2,
                                      fontWeight: 'bold'
                                    }}>PDF</Text>
                                    <Ionicons name="download" size={12} color="#ffffff" style={{ marginTop: 2 }} />
                                  </View>
                                ) : getFileType(publicMedia.media[1].name) === 'audio' ? (
                                  <View style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#9b59b6',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column'
                                  }}>
                                    <View style={profileStyles.audioWaveContainer}>
                                      <View style={[profileStyles.audioWave, { height: 8 }]} />
                                      <View style={[profileStyles.audioWave, { height: 12 }]} />
                                      <View style={[profileStyles.audioWave, { height: 6 }]} />
                                      <View style={[profileStyles.audioWave, { height: 10 }]} />
                                      <View style={[profileStyles.audioWave, { height: 4 }]} />
                                    </View>
                                    {Platform.OS === 'web' && (
                                      <audio src={publicMedia.media[1].url} controls style={{ width: '90%', marginTop: 6 }} />
                                    )}
                                    <Text style={{
                                      color: '#ffffff',
                                      fontSize: 8,
                                      fontFamily: 'VastagoGrotesk',
                                      marginTop: 2,
                                      fontWeight: 'bold'
                                    }}>
                                      {playingAudio === publicMedia.media[1].id ? 'PAUSE' : 'PLAY'}
                                    </Text>
                                  </View>
                                ) : getFileType(publicMedia.media[1].name) === 'video' ? (
                                  <View style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#e67e22',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column'
                                  }}>
                                    <Ionicons name="play-circle" size={24} color="#ffffff" />
                                    <Text style={{
                                      color: '#ffffff',
                                      fontSize: 8,
                                      fontFamily: 'VastagoGrotesk',
                                      marginTop: 2,
                                      fontWeight: 'bold'
                                    }}>VIDEO</Text>
                                  </View>
                                ) : (
                                  <View style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#34495e',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column'
                                  }}>
                                    <Ionicons name="attach" size={24} color="#ffffff" />
                                    <Text style={{
                                      color: '#ffffff',
                                      fontSize: 8,
                                      fontFamily: 'VastagoGrotesk',
                                      marginTop: 2,
                                      fontWeight: 'bold'
                                    }}>FILE</Text>
                                  </View>
                                )}

                                {/* Delete button */}
                                <TouchableOpacity
                                  onPress={() => handlePublicMediaDelete(publicMedia.media[1].id)}
                                  style={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    backgroundColor: 'rgba(255, 0, 0, 0.8)',
                                    borderRadius: 10,
                                    width: 20,
                                    height: 20,
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Text style={{ color: '#ffffff', fontSize: 12, fontWeight: 'bold' }}>×</Text>
                                </TouchableOpacity>
                              </TouchableOpacity>
                            ) : (
                              <TouchableOpacity
                                onPress={() => handlePublicMediaUpload('media2')}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  backgroundColor: `${USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df'}20`,
                                  borderRadius: 8,
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderWidth: 2,
                                  borderColor: `${USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df'}40`,
                                  borderStyle: 'dashed'
                                }}
                              >
                                <Ionicons
                                  name="add-circle"
                                  size={20}
                                  color={USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df'}
                                />
                                <Text style={{
                                  color: USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df',
                                  fontFamily: 'VastagoGrotesk',
                                  fontSize: 10,
                                  marginTop: 2,
                                  textAlign: 'center'
                                }}>Add Media</Text>
                              </TouchableOpacity>
                            )}
                          </View>

                          {/* Media Slot 3 */}
                          <View style={{
                            flex: 1,
                            height: 80,
                            borderRadius: 8,
                            overflow: 'hidden',
                            position: 'relative'
                          }}>
                            {publicMedia.media && publicMedia.media[2] ? (
                              <TouchableOpacity
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  position: 'relative'
                                }}
                                onPress={() => handlePublicMediaPress(2)}
                                onTouchStart={(e) => handleDragStart({ name: publicMedia.media[2].name, url: publicMedia.media[2].url }, 'media', e)}
                                onTouchMove={handleDragMove}
                                onTouchEnd={handleDragEnd}
                                onMouseDown={(e) => handleDragStart({ name: publicMedia.media[2].name, url: publicMedia.media[2].url }, 'media', e)}
                                onMouseMove={handleDragMove}
                                onMouseUp={handleDragEnd}
                              >
                                {getFileType(publicMedia.media[2].name) === 'image' ? (
                                  <Image
                                    source={{ uri: publicMedia.media[2].url }}
                                    style={{ width: '100%', height: '100%' }}
                                    resizeMode="cover"
                                  />
                                ) : getFileType(publicMedia.media[2].name) === 'pdf' ? (
                                  <View style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#e74c3c',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column'
                                  }}>
                                    <Ionicons name="document-text" size={24} color="#ffffff" />
                                    <Text style={{
                                      color: '#ffffff',
                                      fontSize: 8,
                                      fontFamily: 'VastagoGrotesk',
                                      marginTop: 2,
                                      fontWeight: 'bold'
                                    }}>PDF</Text>
                                    <Ionicons name="download" size={12} color="#ffffff" style={{ marginTop: 2 }} />
                                  </View>
                                ) : getFileType(publicMedia.media[2].name) === 'audio' ? (
                                  <View style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#9b59b6',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column'
                                  }}>
                                    <View style={profileStyles.audioWaveContainer}>
                                      <View style={[profileStyles.audioWave, { height: 8 }]} />
                                      <View style={[profileStyles.audioWave, { height: 12 }]} />
                                      <View style={[profileStyles.audioWave, { height: 6 }]} />
                                      <View style={[profileStyles.audioWave, { height: 10 }]} />
                                      <View style={[profileStyles.audioWave, { height: 4 }]} />
                                    </View>
                                    {Platform.OS === 'web' && (
                                      <audio src={publicMedia.media[2].url} controls style={{ width: '90%', marginTop: 6 }} />
                                    )}
                                    <Text style={{
                                      color: '#ffffff',
                                      fontSize: 8,
                                      fontFamily: 'VastagoGrotesk',
                                      marginTop: 2,
                                      fontWeight: 'bold'
                                    }}>
                                      {playingAudio === publicMedia.media[2].id ? 'PAUSE' : 'PLAY'}
                                    </Text>
                                  </View>
                                ) : getFileType(publicMedia.media[2].name) === 'video' ? (
                                  <View style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#e67e22',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column'
                                  }}>
                                    <Ionicons name="play-circle" size={24} color="#ffffff" />
                                    <Text style={{
                                      color: '#ffffff',
                                      fontSize: 8,
                                      fontFamily: 'VastagoGrotesk',
                                      marginTop: 2,
                                      fontWeight: 'bold'
                                    }}>VIDEO</Text>
                                  </View>
                                ) : (
                                  <View style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#34495e',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column'
                                  }}>
                                    <Ionicons name="attach" size={24} color="#ffffff" />
                                    <Text style={{
                                      color: '#ffffff',
                                      fontSize: 8,
                                      fontFamily: 'VastagoGrotesk',
                                      marginTop: 2,
                                      fontWeight: 'bold'
                                    }}>FILE</Text>
                                  </View>
                                )}

                                {/* Delete button */}
                                <TouchableOpacity
                                  onPress={() => handlePublicMediaDelete(publicMedia.media[2].id)}
                                  style={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    backgroundColor: 'rgba(255, 0, 0, 0.8)',
                                    borderRadius: 10,
                                    width: 20,
                                    height: 20,
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Text style={{ color: '#ffffff', fontSize: 12, fontWeight: 'bold' }}>×</Text>
                                </TouchableOpacity>
                              </TouchableOpacity>
                            ) : (
                              <TouchableOpacity
                                onPress={() => handlePublicMediaUpload('media3')}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  backgroundColor: `${USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df'}20`,
                                  borderRadius: 8,
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderWidth: 2,
                                  borderColor: `${USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df'}40`,
                                  borderStyle: 'dashed'
                                }}
                              >
                                <Ionicons
                                  name="add-circle"
                                  size={20}
                                  color={USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df'}
                                />
                                <Text style={{
                                  color: USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df',
                                  fontFamily: 'VastagoGrotesk',
                                  fontSize: 10,
                                  marginTop: 2,
                                  textAlign: 'center'
                                }}>Add Media</Text>
                              </TouchableOpacity>
                            )}
                          </View>
                        </View>
                      </View>

                      <View style={{ width: '100%', marginTop: 20 }}>
                        <Text style={[profileStyles.sectionTitle, { color: '#000000', marginBottom: 10 }]}>Social Links</Text>
                        <View style={{
                          flexDirection: 'row',
                          flexWrap: 'wrap',
                          justifyContent: 'space-between',
                          gap: 10,
                          width: '100%'
                        }}>
                          {Array.isArray(userData.socialLinks) ?
                            userData.socialLinks.slice(0, 4).map((link, index) => (
                              <View key={index} style={{
                                width: '48%',
                                marginBottom: 10,
                                position: 'relative'
                              }}>
                                <TextInput
                                  style={[profileStyles.profileInput, {
                                    width: '100%',
                                    paddingRight: 35,
                                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                    color: '#000000'
                                  }]}
                                  value={link}
                                  placeholder={`Social Link ${index + 1}`}
                                  placeholderTextColor="rgba(0, 0, 0, 0.5)"
                                  onChangeText={(text) => handleUpdateSocialLink(index, text)}
                                  onBlur={handleSave}
                                />
                                <TouchableOpacity
                                  onPress={() => handleRemoveSocialLink(index)}
                                  style={{
                                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                    borderRadius: 12,
                                    padding: 5,
                                    position: 'absolute',
                                    right: 5,
                                    top: 5,
                                    width: 24,
                                    height: 24,
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Text style={{ color: '#000000', fontSize: 16, fontWeight: 'bold' }}>×</Text>
                                </TouchableOpacity>
                              </View>
                            ))
                            : null
                          }
                          {(!Array.isArray(userData.socialLinks) || userData.socialLinks.length < 4) && (
                            <TouchableOpacity
                              onPress={handleAddSocialLink}
                              style={{
                                width: '48%',
                                marginBottom: 10,
                                backgroundColor: `${USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df'}20`,
                                borderRadius: 20,
                                padding: 12,
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 40,
                                transition: 'all 0.3s ease',
                                ...(Platform.OS === 'web' && {
                                  ':hover': {
                                    backgroundColor: `${USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df'}30`,
                                    transform: 'translateY(-2px)'
                                  }
                                })
                              }}
                            >
                              <Text style={{
                                color: USER_TYPES[userData?.userType?.toUpperCase()]?.color || '#0bb5df',
                                fontFamily: 'VastagoGrotesk',
                                fontSize: 14
                              }}>+ Add Social Link</Text>
                            </TouchableOpacity>
                          )}
                        </View>
                      </View>

                      <View style={profileStyles.profileStats}>
                        <Text style={[profileStyles.profileStatText, { color: '#000000' }]}>
                          member since: {userData.memberSince ? new Date(userData.memberSince).toLocaleDateString(undefined, { year: 'numeric', month: 'short' }) : 'N/A'}
                        </Text>
                        <Text style={[profileStyles.profileStatText, { color: '#000000' }]}>
                          {reports.length} submissions
                        </Text>
                      </View>

                      <Text style={{
                        position: 'absolute',
                        bottom: 10,
                        width: '100%',
                        color: 'rgba(0, 0, 0, 0.6)',
                        fontSize: 12,
                        fontStyle: 'italic',
                        fontFamily: 'VastagoGrotesk',
                        textAlign: 'center'
                      }}>Only this box is public</Text>
                    </>
                  )}
                </View>


                {/* On mobile only, render the right card content here */}

                {isMobile && (
                  <View style={[profileStyles.profileBottomRightCard, {
                    maxHeight: 'none',
                    height: 'auto',
                    marginTop: 20,
                    alignSelf: 'center',
                    width: '90vw',
                    padding: 10,
                    paddingTop: 15
                  }]}>
                    <RightCardContent
                      userData={userData}
                      isLibraryMigrated={isProfileLibraryMigrated}
                      activeTab={activeTab}
                      setActiveTab={setActiveTab}
                      renderLegacyLibraryTab={renderLegacyLibraryTab}
                      renderMediaTab={renderMediaTab}
                      renderReportsTab={renderReportsTab}
                      renderSavedVenues={renderSavedVenues}
                      renderRoomEqTab={renderRoomEqTab}
                      renderToursTab={renderToursTab}
                      renderCommunityTab={renderCommunityTab}
                      toursTabVisible={toursTabVisible}
                    />
                  </View>
                )}
              </View>

              {/* On non-mobile, render the right card in its original position */}
              {!isMobile && (
                <View style={profileStyles.profileColumnRight}>
                  <View style={[profileStyles.profileBottomRightCard, {
                    maxHeight: 'none',
                    height: getResponsiveValue({
                      mobile: 'auto',
                      tablet: 'auto',
                      medium: 'auto',
                      laptop: topCardHeight ? topCardHeight : 'auto',
                      desktop: topCardHeight ? topCardHeight : 'auto',
                    }),
                    marginBottom: getResponsiveValue({
                      mobile: 0,
                      tablet: 0,
                      medium: 0,
                      laptop: 20,
                      desktop: 20,
                    }),
                    paddingBottom: 0,
                    alignSelf: 'stretch',
                    display: 'flex',
                    flexDirection: 'column'
                  }]}>
                    <RightCardContent
                      userData={userData}
                      isLibraryMigrated={isProfileLibraryMigrated}
                      activeTab={activeTab}
                      setActiveTab={setActiveTab}
                      renderLegacyLibraryTab={renderLegacyLibraryTab}
                      renderMediaTab={renderMediaTab}
                      renderReportsTab={renderReportsTab}
                      renderSavedVenues={renderSavedVenues}
                      renderRoomEqTab={renderRoomEqTab}
                      renderToursTab={renderToursTab}
                      renderCommunityTab={renderCommunityTab}
                      toursTabVisible={toursTabVisible}
                    />
                  </View>
                </View>
              )}
            </View>
            <View style={[profileStyles.profileGrid, { paddingTop: 0, marginTop: 20 }]}>
              <View
                style={[profileStyles.profileBottomLeftCard, {
                  backgroundColor: isDarkMode ? '#1c1c26' : '#F7EEEE',
                  borderWidth: 3,
                  borderColor: '#0077be',
                  borderStyle: 'solid',
                }]}>

                {/* Account Tabs */}
                <View style={{
                  flexDirection: 'row',
                  marginTop: 15,
                  marginBottom: 20,
                  borderBottomWidth: 2,
                  borderBottomColor: '#0077be',
                  gap: 10
                }}>
                  <TouchableOpacity
                    style={{
                      paddingBottom: 10,
                      borderBottomWidth: accountTab === 'myAccount' ? 3 : 0,
                      borderBottomColor: colors.purpleReplacement,
                      flex: 1,
                      alignItems: 'center'
                    }}
                    onPress={() => setAccountTab('myAccount')}
                  >
                    <Text style={{
                      color: accountTab === 'myAccount' ? colors.purpleReplacement : (isDarkMode ? 'rgba(255,255,255,0.5)' : '#666'),
                      fontSize: 16,
                      fontWeight: accountTab === 'myAccount' ? 'bold' : 'normal',
                      fontFamily: 'VastagoGrotesk'
                    }}>
                      My Account
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={{
                      paddingBottom: 10,
                      borderBottomWidth: accountTab === 'organizations' ? 3 : 0,
                      borderBottomColor: colors.purpleReplacement,
                      flex: 1,
                      alignItems: 'center'
                    }}
                    onPress={() => setAccountTab('organizations')}
                  >
                    <Text style={{
                      color: accountTab === 'organizations' ? colors.purpleReplacement : (isDarkMode ? 'rgba(255,255,255,0.5)' : '#666'),
                      fontSize: 16,
                      fontWeight: accountTab === 'organizations' ? 'bold' : 'normal',
                      fontFamily: 'VastagoGrotesk'
                    }}>
                      Organizations
                    </Text>
                  </TouchableOpacity>
                  {isOwnProfile && (
                    <TouchableOpacity
                      style={{
                        paddingBottom: 10,
                        borderBottomWidth: accountTab === 'myPosts' ? 3 : 0,
                        borderBottomColor: colors.purpleReplacement,
                        flex: 1,
                        alignItems: 'center'
                      }}
                      onPress={() => setAccountTab('myPosts')}
                    >
                      <Text style={{
                        color: accountTab === 'myPosts' ? colors.purpleReplacement : (isDarkMode ? 'rgba(255,255,255,0.5)' : '#666'),
                        fontSize: 16,
                        fontWeight: accountTab === 'myPosts' ? 'bold' : 'normal',
                        fontFamily: 'VastagoGrotesk'
                      }}>
                        My Posts
                      </Text>
                    </TouchableOpacity>
                  )}
                </View>
                {/* Tab Content */}
                {accountTab === 'myAccount' ? (
                  <View style={{ width: '100%', maxWidth: 600, alignSelf: 'center' }}>
                    <Text style={[profileStyles.cardTitle, { color: isDarkMode ? 'rgba(255,255,255,0.75)' : colors.purpleReplacement, marginTop: 15 }]}>My Account</Text>

                    <View style={profileStyles.inputGroup}>
                      <Text style={[profileStyles.inputLabel, { color: isDarkMode ? 'rgba(255,255,255,0.75)' : colors.purpleReplacement }]}>Username</Text>
                      <TextInput
                        style={profileStyles.profileInput}
                        value={userData.username}
                        placeholder={userData.username || 'Enter your username'}
                        placeholderTextColor="#ffffff80"
                        onChangeText={text => setUser({ ...userData, username: text })}
                        onBlur={handleSave}
                      />
                    </View>
                    <View style={profileStyles.inputGroup}>
                      <Text style={[profileStyles.inputLabel, { color: isDarkMode ? 'rgba(255,255,255,0.75)' : colors.purpleReplacement }]}>First Name</Text>
                      <TextInput
                        style={profileStyles.profileInput}
                        value={userData.firstName}
                        placeholder={userData.firstName || 'Enter your first name'}
                        placeholderTextColor="#ffffff80"
                        onChangeText={text => setUser({ ...userData, firstName: text })}
                        onBlur={handleSave}
                      />
                    </View>

                    <View style={profileStyles.inputGroup}>
                      <Text style={[profileStyles.inputLabel, { color: isDarkMode ? 'rgba(255,255,255,0.75)' : colors.purpleReplacement }]}>Last Name</Text>
                      <TextInput
                        style={profileStyles.profileInput}
                        value={userData.lastName}
                        placeholder={userData.lastName || 'Enter your last name'}
                        placeholderTextColor="#ffffff80"
                        onChangeText={text => setUser({ ...userData, lastName: text })}
                        onBlur={handleSave}
                      />
                    </View>

                    <View style={profileStyles.inputGroup}>
                      <Text style={[profileStyles.inputLabel, { color: isDarkMode ? 'rgba(255,255,255,0.75)' : colors.purpleReplacement }]}>Email</Text>
                      <TextInput
                        style={[profileStyles.profileInput, { opacity: 0.7 }]}
                        value={userData.email}
                        placeholder={userData.email || 'Your email'}
                        placeholderTextColor="#ffffff80"
                        editable={false}
                      />
                    </View>

                    <View style={profileStyles.inputGroup}>
                      <Text style={[profileStyles.inputLabel, { color: isDarkMode ? 'rgba(255,255,255,0.75)' : colors.purpleReplacement }]}>User Role</Text>
                      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <TextInput
                          style={[profileStyles.profileInput, { flex: 1, marginRight: 10 }]}
                          value={userTypeInput}
                          placeholder="Enter user role"
                          placeholderTextColor="#ffffff80"
                          onChangeText={setUserTypeInput}
                          onBlur={handleUserTypeUpdate}
                          editable={isOwnProfile}
                        />
                      </View>
                    </View>

                    <View style={{ position: 'relative', zIndex: 1000 }}>
                      <View style={profileStyles.inputGroup}>
                        <Text style={[profileStyles.inputLabel, { color: isDarkMode ? 'rgba(255,255,255,0.75)' : colors.purpleReplacement }]}>User Type</Text>
                        <ProfileDropdown
                          options={ACCOUNT_TYPES}
                          value={accountType}
                          onSelect={handleAccountTypeUpdate}
                          placeholder="Select user type"
                          disabled={!isOwnProfile}
                        />
                      </View>
                    </View>

                    <View style={profileStyles.inputGroup}>
                      <Text style={[profileStyles.inputLabel, { color: isDarkMode ? 'rgba(255,255,255,0.75)' : colors.purpleReplacement }]}>Profile Privacy</Text>
                      <TouchableOpacity
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          backgroundColor: '#434252',
                          padding: 12,
                          borderRadius: 8,
                          marginBottom: 10,
                          zIndex: 0,
                        }}
                        onPress={handlePrivacyToggle}
                      >
                        <Text style={{ color: '#fff', fontSize: 14 }}>
                          {loading ? 'Loading...' : isPublic ? 'Public Profile' : 'Private Profile'}
                        </Text>
                        <View style={{
                          width: 40,
                          height: 24,
                          backgroundColor: isPublic ? '#2ecc71' : '#434252',
                          borderRadius: 12,
                          padding: 2,
                          justifyContent: 'center',
                          borderWidth: isPublic ? 0 : 3,
                          borderColor: 'rgba(255,255,255,0.2)'
                        }}>
                          <View style={{
                            width: 20,
                            height: 20,
                            backgroundColor: '#fff',
                            borderRadius: 10,
                            transform: [{ translateX: isPublic ? 16 : 0 }],
                            transition: 'transform 0.2s ease'
                          }} />
                        </View>
                      </TouchableOpacity>
                      {!isPublic && (
                        <Text style={{
                          color: 'rgba(255,255,255,0.6)',
                          fontSize: 12,
                          marginTop: 4,
                          textAlign: 'center',
                          fontFamily: 'VastagoGrotesk'
                        }}>
                          Your information is only visible to you
                        </Text>
                      )}
                    </View>

                    <View style={profileStyles.inputGroup}>
                      <Text style={[profileStyles.inputLabel, { color: isDarkMode ? 'rgba(255,255,255,0.75)' : colors.purpleReplacement }]}>Discussion Post Anonymization</Text>
                      <TouchableOpacity
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          backgroundColor: '#434252',
                          padding: 12,
                          borderRadius: 8,
                          marginBottom: 10,
                          zIndex: 0,
                          opacity: isAnonymizing ? 0.6 : 1,
                        }}
                        onPress={handleBulkAnonymizeToggle}
                        disabled={isAnonymizing}
                      >
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          {isAnonymizing && (
                            <ActivityIndicator size="small" color="#fff" />
                          )}
                          <Text style={{ color: '#fff', fontSize: 14 }}>
                            {isAnonymizing
                              ? (allPostsAnonymized ? 'Un-anonymizing...' : 'Anonymizing...')
                              : (allPostsAnonymized ? 'All Posts Anonymized' : 'All Posts Visible')
                            }
                          </Text>
                        </View>
                        <View style={{
                          width: 40,
                          height: 24,
                          backgroundColor: allPostsAnonymized ? '#2ecc71' : '#434252',
                          borderRadius: 12,
                          padding: 2,
                          justifyContent: 'center',
                          borderWidth: allPostsAnonymized ? 0 : 3,
                          borderColor: 'rgba(255,255,255,0.2)'
                        }}>
                          <View style={{
                            width: 20,
                            height: 20,
                            backgroundColor: '#fff',
                            borderRadius: 10,
                            transform: [{ translateX: allPostsAnonymized ? 16 : 0 }],
                            transition: 'transform 0.2s ease'
                          }} />
                        </View>
                      </TouchableOpacity>
                      {allPostsAnonymized && (
                        <Text style={{
                          color: 'rgba(255,255,255,0.6)',
                          fontSize: 12,
                          marginTop: 4,
                          textAlign: 'center',
                          fontFamily: 'VastagoGrotesk'
                        }}>
                          All your discussion posts are displayed anonymously
                        </Text>
                      )}
                    </View>

                    <View style={profileStyles.inputGroup}>
                      <Text style={[profileStyles.inputLabel, { color: isDarkMode ? 'rgba(255,255,255,0.75)' : colors.purpleReplacement }]}>Hide Filters on Load</Text>
                      <TouchableOpacity
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          backgroundColor: '#434252',
                          padding: 12,
                          borderRadius: 8,
                          marginBottom: 10,
                          zIndex: 0,
                          opacity: isUpdatingFiltersSetting ? 0.6 : 1,
                        }}
                        onPress={handleHideFiltersToggle}
                        disabled={isUpdatingFiltersSetting}
                      >
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          {isUpdatingFiltersSetting && (
                            <ActivityIndicator size="small" color="#fff" />
                          )}
                          <Text style={{ color: '#fff', fontSize: 14 }}>
                            {isUpdatingFiltersSetting
                              ? (hideFiltersVenueList ? 'Showing filters...' : 'Hiding filters...')
                              : (hideFiltersVenueList ? 'Filters Hidden on Load' : 'Filters Shown on Load')
                            }
                          </Text>
                        </View>
                        <View style={{
                          width: 40,
                          height: 24,
                          backgroundColor: hideFiltersVenueList ? '#2ecc71' : '#434252',
                          borderRadius: 12,
                          padding: 2,
                          justifyContent: 'center',
                          borderWidth: hideFiltersVenueList ? 0 : 3,
                          borderColor: 'rgba(255,255,255,0.2)'
                        }}>
                          <View style={{
                            width: 20,
                            height: 20,
                            backgroundColor: '#fff',
                            borderRadius: 10,
                            transform: [{ translateX: hideFiltersVenueList ? 16 : 0 }],
                            transition: 'transform 0.2s ease'
                          }} />
                        </View>
                      </TouchableOpacity>
                      {hideFiltersVenueList && (
                        <Text style={{
                          color: 'rgba(255,255,255,0.6)',
                          fontSize: 12,
                          marginTop: 4,
                          textAlign: 'center',
                          fontFamily: 'VastagoGrotesk'
                        }}>
                          Filters will be hidden when you open the venue list
                        </Text>
                      )}
                    </View>

                    {isAdminUser && (
                      <View style={profileStyles.inputGroup}>
                        <Text style={[profileStyles.inputLabel, { color: isDarkMode ? 'rgba(255,255,255,0.75)' : colors.purpleReplacement }]}>Settings</Text>
                        <Text style={[profileStyles.inputLabel, { color: isDarkMode ? 'rgba(255,255,255,0.75)' : colors.purpleReplacement, fontSize: 12, marginBottom: 6 }]}>Show version info in corner</Text>
                        <TouchableOpacity
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: '#434252',
                            padding: 12,
                            borderRadius: 8,
                            marginBottom: 10,
                            zIndex: 0,
                          }}
                          onPress={() => setShowVersionWidget(!showVersionWidget)}
                        >
                          <Text style={{ color: '#fff', fontSize: 14 }}>
                            {showVersionWidget ? 'Version widget on' : 'Version widget off'}
                          </Text>
                          <View style={{
                            width: 40,
                            height: 24,
                            backgroundColor: showVersionWidget ? '#2ecc71' : '#434252',
                            borderRadius: 12,
                            padding: 2,
                            justifyContent: 'center',
                            borderWidth: showVersionWidget ? 0 : 3,
                            borderColor: 'rgba(255,255,255,0.2)'
                          }}>
                            <View style={{
                              width: 20,
                              height: 20,
                              backgroundColor: '#fff',
                              borderRadius: 10,
                              transform: [{ translateX: showVersionWidget ? 16 : 0 }],
                              transition: 'transform 0.2s ease'
                            }} />
                          </View>
                        </TouchableOpacity>
                      </View>
                    )}

                    {!hasMigratedProfileCard ? (
                    <InterestsPicker
                      selected={userData.interests || []}
                      onChange={(interests) => {
                        setUser(prev => ({ ...prev, interests }));
                        setTimeout(handleSave, 0);
                      }}
                    />
                    ) : null}

                    <TouchableOpacity
                      style={[profileStyles.signOutButton, { backgroundColor: '#000000' }]}
                      onPress={handleSignOut}
                    >
                      <Text style={[profileStyles.signOutText, { color: '#ffffff' }]}>Sign Out</Text>
                    </TouchableOpacity>

                    {isOwnProfile ? (() => {
                      const subInfo = getSubscriptionDisplayInfo(userData);
                      const hasSubAccess =
                        userData.subscriptionStatus === 'active' ||
                        (userData.subscriptionStatus === 'cancelled' &&
                          userData.subscriptionCancelAt &&
                          new Date(userData.subscriptionCancelAt) > new Date());
                      const subAccent = '#4e7de0';
                      const gloss = glossyFill(subAccent, { isDarkMode, role: 'panel' });
                      const creditsCents = Math.max(0, Number(accountFunds.balanceCents) || 0);
                      const nextApplyCents = 0;
                      const basePriceCents = Number(subInfo.plan?.priceCents) || 0;
                      const nextPayCents =
                        subInfo.active && basePriceCents > 0
                          ? Math.max(0, basePriceCents - nextApplyCents)
                          : null;
                      const nextPayDate = subInfo.cycleEnd
                        ? (() => {
                            try {
                              return new Date(subInfo.cycleEnd).toLocaleDateString(undefined, {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              });
                            } catch (_) {
                              return null;
                            }
                          })()
                        : null;
                      // nextApplyCents is 0 here, so the progress bar stays empty.
                      const applyProgress = 0;
                      const monoLabel = {
                        fontSize: 11,
                        fontWeight: '700',
                        letterSpacing: 1.1,
                        textTransform: 'uppercase',
                        fontFamily: 'VastagoGrotesk',
                      };
                      return (
                        <View
                          style={{
                            marginTop: 12,
                            marginBottom: 6,
                            borderRadius: 12,
                            padding: 18,
                            borderWidth: 1,
                            borderLeftWidth: 3,
                            borderColor: gloss.borderColor || 'rgba(78,125,224,0.35)',
                            borderLeftColor: subAccent,
                            overflow: 'hidden',
                            ...(Platform.OS === 'web'
                              ? glossyWebStyle(gloss)
                              : { backgroundColor: gloss.backgroundColor }),
                          }}
                        >
                          <View
                            style={{
                              flexDirection: 'row',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              gap: 10,
                              marginBottom: 12,
                            }}
                          >
                            <Text style={[monoLabel, { color: gloss.mute }]}>Credits</Text>
                            <TouchableOpacity
                              onPress={
                                hasSubAccess ? handleManageSubscription : handleSubscribe
                              }
                              style={{
                                paddingVertical: 5,
                                paddingHorizontal: 11,
                                borderRadius: 7,
                                borderWidth: 1.5,
                                borderColor: gloss.ink,
                                backgroundColor: 'transparent',
                              }}
                              accessibilityLabel={hasSubAccess ? 'Manage subscription' : 'Subscribe'}
                            >
                              <Text
                                style={{
                                  color: gloss.ink,
                                  fontSize: 12,
                                  fontWeight: '600',
                                  fontFamily: 'VastagoGrotesk',
                                }}
                              >
                                {hasSubAccess ? 'Manage' : 'Subscribe'}
                              </Text>
                            </TouchableOpacity>
                          </View>

                          <Text
                            style={{
                              fontSize: 32,
                              fontWeight: '600',
                              color: gloss.ink,
                              letterSpacing: -0.7,
                              fontFamily: 'VastagoGrotesk',
                            }}
                          >
                            {accountFunds.loading ? '…' : formatFundsCents(creditsCents)}
                          </Text>
                          <Text
                            style={{
                              color: gloss.mute,
                              fontSize: 13,
                              marginTop: 4,
                              fontFamily: 'VastagoGrotesk',
                            }}
                          >
                            {subInfo.planId === PLAN_IDS.VRC_MEMBER
                              ? `Credits balance · ${formatFundsCents(creditsCents)} — exchange to Wallet monthly`
                              : 'Credits available when subscribed'}
                          </Text>

                          {subInfo.planId === PLAN_IDS.VRC_MEMBER && creditsCents > 0 ? (
                            <View
                              style={{
                                height: 6,
                                borderRadius: 3,
                                marginTop: 12,
                                overflow: 'hidden',
                                backgroundColor: gloss.line || 'rgba(0,0,0,0.12)',
                              }}
                            >
                              <View
                                style={{
                                  height: '100%',
                                  width: `${Math.round(applyProgress * 100)}%`,
                                  borderRadius: 3,
                                  backgroundColor: gloss.ink,
                                }}
                              />
                            </View>
                          ) : null}

                          <View
                            style={{
                              marginTop: 14,
                              paddingTop: 12,
                              borderTopWidth: 1,
                              borderTopColor: gloss.line || 'rgba(0,0,0,0.1)',
                              gap: 8,
                            }}
                          >
                            <View
                              style={{
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                gap: 8,
                              }}
                            >
                              <Text style={[monoLabel, { color: gloss.mute }]}>Plan</Text>
                              <Text
                                style={{
                                  color: gloss.ink,
                                  fontSize: 13.5,
                                  fontWeight: '600',
                                  fontFamily: 'VastagoGrotesk',
                                  textAlign: 'right',
                                  flex: 1,
                                }}
                                numberOfLines={1}
                              >
                                {subInfo.active
                                  ? subInfo.planName || 'Subscription'
                                  : 'No active plan'}
                              </Text>
                            </View>

                            {subInfo.active && nextPayCents != null ? (
                              <View
                                style={{
                                  flexDirection: 'row',
                                  justifyContent: 'space-between',
                                  gap: 8,
                                }}
                              >
                                <Text style={[monoLabel, { color: gloss.mute }]}>Next payment</Text>
                                <Text
                                  style={{
                                    color: gloss.ink,
                                    fontSize: 13.5,
                                    fontWeight: '600',
                                    fontFamily: 'VastagoGrotesk',
                                    textAlign: 'right',
                                    flex: 1,
                                  }}
                                >
                                  {formatCentsAsDollars(nextPayCents)}
                                  {nextPayDate ? ` · ${nextPayDate}` : ''}
                                  {nextApplyCents > 0
                                    ? ` (−${formatFundsCents(nextApplyCents)})`
                                    : ''}
                                </Text>
                              </View>
                            ) : (
                              <Text
                                style={{
                                  color: gloss.mute,
                                  fontSize: 12.5,
                                  lineHeight: 18,
                                  fontFamily: 'VastagoGrotesk',
                                }}
                              >
                                Subscribe to VRC Member to earn and exchange Credits to your Wallet.
                              </Text>
                            )}

                            {accountPendingCredits.pendingCount > 0 ? (
                              <Text
                                style={{
                                  color: gloss.mute,
                                  fontSize: 12.5,
                                  marginTop: 2,
                                  fontFamily: 'VastagoGrotesk',
                                }}
                              >
                                +{formatFundsCents(accountPendingCredits.pendingCents)} pending review
                              </Text>
                            ) : null}

                            {userData.subscriptionStatus === 'cancelled' &&
                            userData.subscriptionCancelAt &&
                            new Date(userData.subscriptionCancelAt) > new Date() ? (
                              <Text
                                style={{
                                  color: gloss.mute,
                                  fontSize: 12.5,
                                  fontFamily: 'VastagoGrotesk',
                                }}
                              >
                                Access ends{' '}
                                {new Date(userData.subscriptionCancelAt).toLocaleDateString()}
                              </Text>
                            ) : null}
                          </View>
                        </View>
                      );
                    })() : null}

                    <TouchableOpacity
                      style={{
                        marginTop: 10,
                        opacity: 0.5,
                        backgroundColor: 'transparent',
                        padding: 10,
                        borderRadius: 5,
                        alignItems: 'center'
                      }}
                      onPress={() => setShowPasswordResetModal(true)}
                    >
                      <Text style={{
                        fontSize: 14,
                        color: '#4caf50',
                        fontFamily: 'VastagoGrotesk'
                      }}>Reset Password</Text>
                    </TouchableOpacity>
                  </View>
                ) : accountTab === 'organizations' ? (
                  <View style={{ width: '100%', maxWidth: 600, alignSelf: 'center' }}>
                    <OrganizationsTab
                      userData={userData}
                      organizations={organizations}
                      setOrganizations={setOrganizations}
                      organizationInvitations={organizationInvitations}
                      setOrganizationInvitations={setOrganizationInvitations}
                      showCreateOrgModal={showCreateOrgModal}
                      setShowCreateOrgModal={setShowCreateOrgModal}
                      newOrgName={newOrgName}
                      setNewOrgName={setNewOrgName}
                      newOrgEmail={newOrgEmail}
                      setNewOrgEmail={setNewOrgEmail}
                      newOrgDescription={newOrgDescription}
                      setNewOrgDescription={setNewOrgDescription}
                      selectedOrganization={selectedOrganization}
                      setSelectedOrganization={setSelectedOrganization}
                      showInviteModal={showInviteModal}
                      setShowInviteModal={setShowInviteModal}
                      inviteEmail={inviteEmail}
                      setInviteEmail={setInviteEmail}
                      colors={colors}
                      profileStyles={profileStyles}
                    />
                  </View>
                ) : accountTab === 'myPosts' && isOwnProfile ? (
                  <ProfileMyPostsTab
                    navigation={navigation}
                    userData={userData}
                    colors={colors}
                    profileStyles={profileStyles}
                  />
                ) : null}
              </View>
            </View>
          </View>
        </ScrollView>
        <StatusBar style="light" />

        {/* Delete Photo Confirmation Modal */}
        <Modal
          visible={showDeleteConfirmModal !== null}
          transparent={true}
          animationType="fade"
          onRequestClose={() => setShowDeleteConfirmModal(null)}
        >
          <View style={{
            flex: 1,
            backgroundColor: 'rgba(0,0,0,0.5)',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20
          }}>
            <View style={{
              backgroundColor: '#ffffff',
              borderRadius: 12,
              padding: 20,
              width: '100%',
              maxWidth: 400,
              alignItems: 'center'
            }}>
              <Ionicons name="trash" size={48} color="#e74c3c" />
              <Text style={{
                fontSize: 18,
                fontFamily: 'Glacial Indifference',
                marginTop: 10,
                marginBottom: 15,
                textAlign: 'center',
                color: '#e74c3c'
              }}>Delete Photo</Text>

              <Text style={{
                fontSize: 16,
                marginBottom: 10,
                textAlign: 'center',
                color: '#333',
                fontFamily: 'Glacial Indifference'
              }}>
                Are you sure you want to delete this photo?
              </Text>

              <View style={{
                backgroundColor: '#f8f9fa',
                borderRadius: 8,
                padding: 12,
                marginBottom: 20,
                borderLeftWidth: 4,
                borderLeftColor: '#e74c3c'
              }}>
                <Text style={{
                  fontSize: 14,
                  fontFamily: 'Glacial Indifference',
                  color: '#333',
                  fontWeight: 'bold'
                }}>"{showDeleteConfirmModal}"</Text>
              </View>

              <Text style={{
                fontSize: 12,
                color: '#666',
                textAlign: 'center',
                marginBottom: 20,
                fontStyle: 'italic'
              }}>
                This action cannot be undone. The photo will be permanently deleted.
              </Text>

              <View style={{
                flexDirection: 'row',
                gap: 10,
                width: '100%'
              }}>
                <TouchableOpacity
                  onPress={() => setShowDeleteConfirmModal(null)}
                  style={{
                    flex: 1,
                    padding: 12,
                    borderRadius: 8,
                    backgroundColor: '#f0f0f0',
                    alignItems: 'center'
                  }}
                >
                  <Text style={{
                    fontSize: 16,
                    fontFamily: 'Glacial Indifference',
                    color: '#333'
                  }}>Cancel</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={() => {
                    if (showDeleteConfirmModal) {
                      handleMediaDelete(showDeleteConfirmModal);
                      setShowDeleteConfirmModal(null);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: 12,
                    borderRadius: 8,
                    backgroundColor: '#e74c3c',
                    alignItems: 'center'
                  }}
                >
                  <Text style={{
                    color: '#ffffff',
                    fontSize: 16,
                    fontFamily: 'Glacial Indifference',
                    fontWeight: 'bold'
                  }}>Delete</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>

        {/* Password Reset Modal */}
        <Modal
          visible={showPasswordResetModal}
          transparent={true}
          animationType="fade"
          onRequestClose={() => setShowPasswordResetModal(false)}
        >
          <View style={{
            flex: 1,
            backgroundColor: 'rgba(0,0,0,0.5)',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20
          }}>
            <View style={{
              backgroundColor: '#ffffff',
              borderRadius: 12,
              padding: 20,
              width: '100%',
              maxWidth: 400,
              alignItems: 'center'
            }}>
              <Ionicons name="lock-closed" size={48} color="#4e7de0" />
              <Text style={{
                fontSize: 18,
                fontFamily: 'Glacial Indifference',
                marginTop: 10,
                marginBottom: 15,
                textAlign: 'center',
                color: '#4e7de0'
              }}>Reset Password</Text>

              <Text style={{
                fontSize: 16,
                marginBottom: 20,
                textAlign: 'center',
                color: '#333',
                fontFamily: 'Glacial Indifference'
              }}>
                Enter your email address to receive a password reset link
              </Text>

              <TextInput
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 8,
                  backgroundColor: '#f8f9fa',
                  borderWidth: 1,
                  borderColor: '#e9ecef',
                  fontSize: 16,
                  fontFamily: 'Glacial Indifference',
                  marginBottom: 20,
                  color: '#333'
                }}
                placeholder="Enter your email"
                placeholderTextColor="#666"
                value={emailInput}
                onChangeText={setEmailInput}
                keyboardType="email-address"
                autoCapitalize="none"
              />

              <View style={{
                flexDirection: 'row',
                gap: 10,
                width: '100%'
              }}>
                <TouchableOpacity
                  onPress={() => setShowPasswordResetModal(false)}
                  style={{
                    flex: 1,
                    padding: 12,
                    borderRadius: 8,
                    backgroundColor: '#f0f0f0',
                    alignItems: 'center'
                  }}
                >
                  <Text style={{
                    fontSize: 16,
                    fontFamily: 'Glacial Indifference',
                    color: '#333'
                  }}>Cancel</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={handlePasswordReset}
                  style={{
                    flex: 1,
                    padding: 12,
                    borderRadius: 8,
                    backgroundColor: '#4e7de0',
                    alignItems: 'center'
                  }}
                >
                  <Text style={{
                    color: '#ffffff',
                    fontSize: 16,
                    fontFamily: 'Glacial Indifference',
                    fontWeight: 'bold'
                  }}>Send Reset Link</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>
      </View>
    </View>
  );
} 